<template>
  <v-container fluid class="view-page">
    <AError :api="apiDeleteApplication" />

    <!-- Send Email Reminder dialog -->
    <EmailReminderDialog
      v-model="emailReminderDialog"
      :application-uuid="$route.query._ausk"
      :service-url="$service[service.key]"
    />

    <!-- Flagged case banner -->
    <transition name="fade">
      <div v-if="applicationDetails.isFlag" class="flag-banner">
        <div class="flag-banner__icon">
          <v-icon size="18" color="#fff">mdi-flag</v-icon>
        </div>
        <div class="flag-banner__content">
          <span class="flag-banner__title">This case is being flagged</span>
          <span v-if="flagRemarks" class="flag-banner__remarks">{{ flagRemarks }}</span>
        </div>
      </div>
    </transition>

    <!-- Flag / Error alerts -->
    <AAlertBanner variant="danger" :messages="alertError" title="Warning" :icon="alertErrorIcon" :icon-size="22" />

    <!-- Approve application dialog -->
    <v-dialog v-model="updateStatusDialog" max-width="460" content-class="approve-dialog">
      <div class="approve-card">
        <h3 class="approve-card__title">Approve Application</h3>
        <p class="approve-card__message">
          Are you sure to update the status to
          <span class="font-weight-bold">{{ updateStatusText }}</span>?
        </p>

        <div class="approve-card__field">
          <label class="approve-card__label">Customer Service (optional)</label>
          <div class="approve-select" v-click-outside="() => csDropdownOpen = false">
            <div
              class="approve-select__trigger"
              :class="{ 'approve-select__trigger--open': csDropdownOpen, 'approve-select__trigger--error': errorField.customerServiceSelect }"
              @click="csDropdownOpen = !csDropdownOpen"
            >
              <div class="approve-select__value">
                <template v-if="customerServiceDetails.customerServiceSelect.length > 0">
                  <span
                    v-for="(val, i) in customerServiceDetails.customerServiceSelect"
                    :key="i"
                    class="approve-select__chip"
                  >
                    {{ getCustomerServiceText(val) }}
                    <button
                      class="approve-select__chip-remove"
                      @click.stop="removeCustomerService(i)"
                    >
                      <v-icon size="14" color="var(--app-text-muted)">mdi-close</v-icon>
                    </button>
                  </span>
                </template>
                <span v-else class="approve-select__placeholder">Select customer service...</span>
              </div>
              <v-icon size="20" color="var(--app-text-faint)" class="approve-select__arrow" :class="{ 'approve-select__arrow--open': csDropdownOpen }">mdi-chevron-down</v-icon>
            </div>
            <transition name="approve-dropdown">
              <div v-if="csDropdownOpen" class="approve-select__dropdown">
                <div
                  v-for="item in customerServiceItems"
                  :key="item.value"
                  class="approve-select__option"
                  :class="{ 'approve-select__option--selected': customerServiceDetails.customerServiceSelect.includes(item.value) }"
                  @click="toggleCustomerService(item.value)"
                >
                  <div class="approve-select__checkbox" :class="{ 'approve-select__checkbox--checked': customerServiceDetails.customerServiceSelect.includes(item.value) }">
                    <v-icon v-if="customerServiceDetails.customerServiceSelect.includes(item.value)" size="14" color="#fff">mdi-check</v-icon>
                  </div>
                  <span>{{ item.text }}</span>
                </div>
                <div v-if="customerServiceItems.length === 0" class="approve-select__empty">
                  No options available
                </div>
              </div>
            </transition>
          </div>
          <div v-if="errorField.customerServiceSelect" class="approve-select__error">
            {{ Array.isArray(errorField.customerServiceSelect) ? errorField.customerServiceSelect.join(', ') : errorField.customerServiceSelect }}
          </div>
        </div>

        <div class="approve-card__actions">
          <button
            class="approve-card__btn approve-card__btn--cancel"
            @click="updateStatusDialog = false; csDropdownOpen = false"
          >
            Cancel
          </button>
          <button
            class="approve-card__btn approve-card__btn--confirm"
            @click="modalUpdateApplicationStatus()"
          >
            Approve
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Complete application dialog -->
    <TheModalConfirmation
      ref="completeApplicationConfirmPopup"
      @agree="agreeCallbacCompleteApplication()"
      @disagree="disagreeCallbacCompleteApplication()"
    ></TheModalConfirmation>

    <!-- Delete application dialog -->
    <v-dialog v-model="deleteApplicationDialog" max-width="420" content-class="confirm-dialog">
      <div class="confirm-card">
        <h3 class="confirm-card__title">Delete Application</h3>
        <p class="confirm-card__message">
          Are you sure to delete this application?
        </p>
        <p class="confirm-card__hint">*Deleted application can still be restored by admin</p>
        <div class="confirm-card__actions">
          <button class="confirm-card__btn confirm-card__btn--cancel" @click="deleteApplicationDialog = false">
            Cancel
          </button>
          <button class="confirm-card__btn confirm-card__btn--danger" @click="deleteApplication()">
            Delete
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Delete attachment dialog -->
    <v-dialog v-model="deleteAttachmentDialog" max-width="420" content-class="confirm-dialog">
      <div class="confirm-card">
        <h3 class="confirm-card__title">Delete Attachment</h3>
        <p class="confirm-card__message">Are you sure to delete this attachment?</p>
        <p class="confirm-card__hint">*Deleted attachment can still be restored by admin</p>
        <div class="confirm-card__actions">
          <button class="confirm-card__btn confirm-card__btn--cancel" @click="toggleDeleteAttachmentDialog(false)">
            Cancel
          </button>
          <button class="confirm-card__btn confirm-card__btn--danger" @click="deleteAttachment()">
            Delete
          </button>
        </div>
      </div>
    </v-dialog>

    <v-dialog
      v-model="generalRemarksDialog"
      max-width="500"
      content-class="remarks-dialog"
    >
      <div class="remarks-card">
        <!-- Header -->
        <div class="remarks-card__header">
          <div class="remarks-card__header-left">
            <div class="remarks-card__icon">
              <v-icon color="var(--app-accent)" size="22">mdi-note-edit-outline</v-icon>
            </div>
            <div>
              <div class="remarks-card__title">General Remarks</div>
              <div class="remarks-card__subtitle">Add or update your general remarks</div>
            </div>
          </div>
          <button class="remarks-card__close" @click="generalRemarksDialog = false">
            <v-icon size="20" color="var(--app-text-faint)">mdi-close</v-icon>
          </button>
        </div>

        <!-- Body -->
        <div class="remarks-card__body">
          <!-- Error alert -->
          <div v-if="apiSubmitGeneralRemarks.error" class="remarks-card__error-alert">
            <v-icon size="18" color="#dc2626" class="mr-2">mdi-alert-circle-outline</v-icon>
            <span>{{ apiSubmitGeneralRemarks.error }}</span>
          </div>

          <label class="remarks-card__label">Remark</label>
          <textarea
            v-model="otherDetails.generalRemarks"
            class="remarks-card__textarea"
            rows="5"
            placeholder="Enter your general remarks here..."
          ></textarea>
        </div>

        <!-- Footer -->
        <div class="remarks-card__footer">
          <button
            class="remarks-card__btn remarks-card__btn--cancel"
            @click="generalRemarksDialog = false"
          >
            Cancel
          </button>
          <button
            class="remarks-card__btn remarks-card__btn--confirm"
            @click="submitGeneralRemarks()"
          >
            Update
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- View email dialog -->
    <v-dialog
      transition="dialog-bottom-transition"
      max-width="900"
      v-model="emailDialog"
      content-class="email-dialog"
    >
      <div class="email-modal">
        <!-- Header -->
        <div class="email-modal__header">
          <div class="email-modal__header-left">
            <div class="email-modal__icon">
              <v-icon color="var(--app-accent)" size="22">mdi-email-outline</v-icon>
            </div>
            <div>
              <div class="email-modal__title">{{ emailModalDetails.emailModalHeader }}</div>
              <div class="email-modal__subject">{{ emailModalDetails.emailModalSubject }}</div>
            </div>
          </div>
          <button class="email-modal__close" @click="emailDialog = false">
            <v-icon size="20" color="var(--app-text-faint)">mdi-close</v-icon>
          </button>
        </div>

        <!-- Recipients -->
        <div class="email-modal__recipients">
          <div class="email-modal__recipient-row">
            <span class="email-modal__recipient-label">To</span>
            <div class="email-modal__recipient-chips">
              <span
                v-for="(toRecipient, index) in emailModalDetails.emailModalToRecipient"
                :key="'to-' + index"
                class="email-modal__chip"
              >{{ toRecipient }}</span>
            </div>
          </div>
          <div class="email-modal__recipient-row" v-if="emailModalDetails.emailModalCcRecipient && emailModalDetails.emailModalCcRecipient.length > 0">
            <span class="email-modal__recipient-label">Cc</span>
            <div class="email-modal__recipient-chips">
              <span
                v-for="(ccRecipient, index) in emailModalDetails.emailModalCcRecipient"
                :key="'cc-' + index"
                class="email-modal__chip email-modal__chip--cc"
              >{{ ccRecipient }}</span>
            </div>
          </div>
        </div>

        <!-- Email Body -->
        <div class="email-modal__body">
          <div v-html="emailModalDetails.emailModalContent"></div>
        </div>

        <!-- Footer -->
        <div class="email-modal__footer">
          <button class="email-modal__btn" @click="emailDialog = false">Close</button>
        </div>
      </div>
    </v-dialog>

    <!-- View Application Log dialog -->
    <v-dialog
      transition="dialog-bottom-transition"
      max-width="1100"
      v-model="viewLogDialog"
      content-class="log-dialog"
    >
      <div class="log-modal">
        <!-- Header -->
        <div class="log-modal__header">
          <div class="log-modal__header-left">
            <div class="log-modal__icon">
              <v-icon size="22" color="var(--app-accent)">mdi-clipboard-text-clock-outline</v-icon>
            </div>
            <div>
              <h2 class="log-modal__title">Application Log</h2>
              <p class="log-modal__subtitle">Track all status changes and activity</p>
            </div>
          </div>
          <button class="log-modal__close" @click="viewLogDialog = false">
            <v-icon size="20" color="var(--app-text-faint)">mdi-close</v-icon>
          </button>
        </div>

        <!-- Tabs -->
        <div class="log-tabs">
          <button
            class="log-tab"
            :class="{ 'log-tab--active': logTab === 'tab-1' }"
            @click="logTab = 'tab-1'"
          >
            <v-icon
              size="16"
              :color="logTab === 'tab-1' ? 'var(--app-accent)' : 'var(--app-text-faint)'"
              class="mr-2"
            >mdi-text-box-edit</v-icon>
            Application Logs
            <span class="log-tab__count" v-if="applicationLogs.length">{{ applicationLogs.length }}</span>
          </button>
          <button
            class="log-tab"
            :class="{ 'log-tab--active': logTab === 'tab-2' }"
            @click="logTab = 'tab-2'"
          >
            <v-icon
              size="16"
              :color="logTab === 'tab-2' ? 'var(--app-accent)' : 'var(--app-text-faint)'"
              class="mr-2"
            >mdi-text-box-check-outline</v-icon>
            General Logs
            <span class="log-tab__count" v-if="generalLogs.length">{{ generalLogs.length }}</span>
          </button>
        </div>

        <!-- Table content -->
        <div class="log-modal__body">
          <!-- Application Logs -->
          <div v-if="logTab === 'tab-1'">
            <div class="log-table-layout" v-if="applicationLogs.length > 0">
              <!-- Left timeline rail: start (top) → arrow (bottom) -->
              <div class="log-rail">
                <span class="log-rail-cap log-rail-cap--start">Start</span>
                <span class="log-rail-dot"></span>
                <span class="log-rail-track"></span>
                <span class="log-rail-arrow"></span>
                <span class="log-rail-cap log-rail-cap--end">Latest</span>
              </div>
              <div class="log-table-wrap">
                <table class="log-table">
                  <thead>
                    <tr>
                      <th>Performed By</th>
                      <th>Status</th>
                      <th>Scheduled Date</th>
                      <th>Created Date</th>
                      <th>Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(stage, index) in applicationLogs"
                      :key="`app-${stage.id}-${index}`"
                    >
                      <td data-label="Performed By">
                        <span class="log-user">{{ stage.userUuid }}</span>
                      </td>
                      <td data-label="Status">
                        <span class="log-status-pill">{{ stage.stageName }}</span>
                      </td>
                      <td class="log-date" data-label="Scheduled Date">{{ stage.scheduledDate }}</td>
                      <td class="log-date" data-label="Created Date">{{ stage.createdDate }}</td>
                      <td class="log-date" data-label="Last Updated">{{ stage.updatedDate }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="log-empty">No application logs available</div>
          </div>

          <!-- General Logs -->
          <div v-if="logTab === 'tab-2'">
            <div class="log-table-layout" v-if="generalLogs.length > 0">
              <!-- Left timeline rail: start (top) → arrow (bottom) -->
              <div class="log-rail">
                <span class="log-rail-cap log-rail-cap--start">Start</span>
                <span class="log-rail-dot"></span>
                <span class="log-rail-track"></span>
                <span class="log-rail-arrow"></span>
                <span class="log-rail-cap log-rail-cap--end">Latest</span>
              </div>
              <div class="log-table-wrap">
                <table class="log-table">
                  <thead>
                    <tr>
                      <th>Performed By</th>
                      <th>Status</th>
                      <th>Scheduled Date</th>
                      <th>Created Date</th>
                      <th>Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(stage, index) in generalLogs"
                      :key="`gen-${stage.id}-${index}`"
                    >
                      <td data-label="Performed By">
                        <span class="log-user">{{ stage.userUuid }}</span>
                      </td>
                      <td data-label="Status">
                        <span class="log-status-pill">{{ stage.stageName }}</span>
                      </td>
                      <td class="log-date" data-label="Scheduled Date">{{ stage.scheduledDate }}</td>
                      <td class="log-date" data-label="Created Date">{{ stage.createdDate }}</td>
                      <td class="log-date" data-label="Last Updated">{{ stage.updatedDate }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="log-empty">No general logs available</div>
          </div>
        </div>

        <!-- Footer -->
        <div class="log-modal__footer">
          <span class="log-modal__footer-info">
            {{ logTab === 'tab-1' ? applicationLogs.length : generalLogs.length }} records
          </span>
          <button class="log-modal__footer-btn" @click="viewLogDialog = false">Close</button>
        </div>
      </div>
    </v-dialog>

    <!-- Add a flag remark dialog -->
    <v-dialog
      v-model="flagDialog"
      max-width="500"
      content-class="flag-dialog"
      @click:outside="clearErrorMsg()"
    >
      <div class="flag-card">
        <!-- Header -->
        <div class="flag-header">
          <div class="flag-header__left">
            <div class="flag-header__icon">
              <v-icon color="var(--app-accent)" size="22">mdi-flag-outline</v-icon>
            </div>
            <div>
              <div class="flag-header__title">Add a Remark</div>
              <div class="flag-header__subtitle">Flag case is required to put remarks</div>
            </div>
          </div>
          <button class="flag-header__close" @click="closeUpdateFlagAndRemarkDialog()">
            <v-icon size="20" color="var(--app-text-faint)">mdi-close</v-icon>
          </button>
        </div>

        <!-- Body -->
        <div class="flag-body">
          <!-- Error alert -->
          <div v-if="api.error" class="flag-alert-error">
            <v-icon size="18" color="#dc2626" class="flag-alert-error__icon">mdi-alert-circle-outline</v-icon>
            <span class="flag-alert-error__text">{{ api.error }}</span>
          </div>

          <!-- Remark textarea -->
          <label class="flag-label">Remark</label>
          <textarea
            v-model="flagRemarks"
            class="flag-textarea"
            :class="{ 'flag-textarea--error': errorField.flagRemarks }"
            rows="4"
            placeholder="Enter your remark here..."
          ></textarea>
          <div v-if="errorField.flagRemarks" class="flag-field-error">
            {{ Array.isArray(errorField.flagRemarks) ? errorField.flagRemarks.join(', ') : errorField.flagRemarks }}
          </div>
        </div>

        <!-- Footer buttons -->
        <div class="flag-footer">
          <button
            class="flag-btn flag-btn--cancel"
            @click="closeUpdateFlagAndRemarkDialog()"
          >
            Cancel
          </button>
          <button
            v-if="flagOrUnflagText == 'Unflag'"
            class="flag-btn flag-btn--outline"
            @click="updateFlagRemarkOnly()"
          >
            Update Flag Remark
          </button>
          <button
            class="flag-btn flag-btn--primary"
            @click="updateFlagAndRemark()"
          >
            {{ flagOrUnflagText }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Restore trash application dialog -->
    <v-dialog v-model="restoreApplicationDialog" max-width="500">
      <v-card>
        <v-toolbar :color="HRDC_BLUE_THEME_COLOR" dark
          >Restore Application</v-toolbar
        >

        <div class="pa-5">
          <AError :api="apiRestoreApplication" />
          Are you sure to restore this application?
        </div>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="green lighten-1"
            class="text-light"
            @click="restoreTrashApplication()"
          >
            Restore
          </v-btn>
          <v-btn
            color="red lighten-1"
            class="text-light"
            @click="restoreApplicationDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- To handle attachment file action menu-->
    <v-menu
      v-model="showAttachmentAction.menu"
      :position-x="attachmentX"
      :position-y="attachmentY"
      absolute
      offset-y
      content-class="atch-menu"
    >
      <div class="atch-menu__card">
        <div
          v-if="showAttachmentAction.view"
          class="atch-menu__item"
          @click.prevent="previewOrDownloadAttachment(attachmentPointingItem, 'preview')"
        >
          <v-icon size="16" color="var(--app-accent)" class="mr-3">mdi-eye-outline</v-icon>
          <span>View</span>
        </div>
        <div
          v-if="showAttachmentAction.view"
          class="atch-menu__item"
          @click.prevent="copyAttachmentShareLink(attachmentPointingItem)"
        >
          <v-icon size="16" color="var(--app-accent)" class="mr-3">mdi-link-variant</v-icon>
          <span>Copy link</span>
        </div>
        <div
          v-if="showAttachmentAction.download"
          class="atch-menu__item"
          @click.prevent="previewOrDownloadAttachment(attachmentPointingItem, 'download')"
        >
          <v-icon size="16" color="#8b5cf6" class="mr-3">mdi-download-outline</v-icon>
          <span>Download</span>
        </div>
        <div
          v-if="showAttachmentAction.email"
          class="atch-menu__item"
          @click="getEmailContent(attachmentPointingItem)"
        >
          <v-icon size="16" color="#10b981" class="mr-3">mdi-email-outline</v-icon>
          <span>Email</span>
        </div>
        <div
          v-if="isHrdcSuperAdminRoles"
          class="atch-menu__divider"
        ></div>
        <div
          v-if="isHrdcSuperAdminRoles"
          class="atch-menu__item atch-menu__item--danger"
          @click="toggleDeleteAttachmentDialog(true, attachmentPointingItem)"
        >
          <v-icon size="16" color="#ef4444" class="mr-3">mdi-delete-outline</v-icon>
          <span>Delete</span>
        </div>
      </div>
    </v-menu>

    <!-- Header -->
    <div class="view-header">
      <div class="view-header__top">

        <div v-if="!loadingDialog" class="view-header__info">
          <h1 class="view-header__title">{{ model.view.name }}</h1>
          <div class="view-header__status">
            <span class="status-badge" :style="{ backgroundColor: alertColor || '#1565c0' }">
              <v-icon x-small dark>mdi-information-outline</v-icon>
              {{ pendingAction().pending }}
            </span>
          </div>
        </div>

        <div class="view-header__actions">
          <!-- Flag Case -->
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <button
                class="hero-action-btn"
                :class="applicationDetails.isFlag ? 'hero-action-btn--danger' : 'hero-action-btn--orange'"
                @click="openUpdateFlagModal()"
                v-if="displayDeletedApplication"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="18" :color="applicationDetails.isFlag ? '#ef4444' : '#94a3b8'">
                  {{ flaggedCase.mdiIcon }}
                </v-icon>
              </button>
            </template>
            <span>Flag Case</span>
          </v-tooltip>

          <!-- Approve Application -->
          <v-tooltip top v-if="rolesAllowed && displayDeletedApplication">
            <template v-slot:activator="{ on, attrs }">
              <button
                class="hero-action-btn hero-action-btn--amber"
                @click.stop="updateStatusDialog = true"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="18" color="#f59e0b">mdi-check-decagram-outline</v-icon>
              </button>
            </template>
            <span>Approve Application</span>
          </v-tooltip>

          <!-- Next Action -->
          <v-tooltip top v-if="nextActionDisplay && displayDeletedApplication">
            <template v-slot:activator="{ on, attrs }">
              <button
                class="hero-action-btn hero-action-btn--emerald"
                @click.stop="nextAction()"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="18">mdi-arrow-right</v-icon>
              </button>
            </template>
            <span>Next Action</span>
          </v-tooltip>

          <!-- View Log -->
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <button
                class="hero-action-btn hero-action-btn--blue"
                @click="viewLogDialog = true"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="18" color="#3b82f6">mdi-clock-outline</v-icon>
              </button>
            </template>
            <span>View Log</span>
          </v-tooltip>

          <!-- Email Reminder -->
          <v-tooltip top v-if="false">
            <template v-slot:activator="{ on, attrs }">
              <button
                class="hero-action-btn hero-action-btn--sky"
                @click="emailReminderDialog = true"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="18" color="var(--app-accent)">mdi-email-fast-outline</v-icon>
              </button>
            </template>
            <span>Email Reminder</span>
          </v-tooltip>

          <!-- Restore Application -->
          <v-tooltip top v-if="!displayDeletedApplication">
            <template v-slot:activator="{ on, attrs }">
              <button
                class="hero-action-btn hero-action-btn--teal"
                @click="restoreApplicationDialog = true"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="18" color="#14b8a6">mdi-delete-restore</v-icon>
              </button>
            </template>
            <span>Restore Application</span>
          </v-tooltip>

          <!-- More actions menu -->
          <v-menu
            offset-y
            rounded="lg"
            transition="slide-y-transition"
            max-height="300"
            v-if="displayDeletedApplication"
          >
            <template v-slot:activator="{ on, attrs }">
              <button class="hero-action-btn hero-action-btn--purple" v-bind="attrs" v-on="on">
                <v-icon size="18" color="var(--app-text-muted)">mdi-dots-vertical</v-icon>
              </button>
            </template>

            <v-card class="action-dropdown" elevation="8">
              <v-list dense class="py-2">
                <v-list-item
                  v-for="(action, index) in actions"
                  :key="index"
                  link
                  class="action-dropdown__item"
                  @click="redirectForm(action)"
                >
                  <v-list-item-title class="action-dropdown__text">{{ action.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
        </div>
      </div>

      <!-- Progress Bar -->
      <div class="progress-wrap">
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: pendingAction().progressPercentage + '%' }"></div>
        </div>
      </div>
    </div>

    <div id="fab-speed-dial" v-if="!loadingDialog">
      <div class="cards-grid">
        <!-- LEFT COLUMN -->
        <div class="cards-column">
          <!-- Company Details -->
          <div class="section-card">
            <div class="section-header">
              <v-icon color="white" small>mdi-office-building-outline</v-icon>
              <span class="section-header__title">Company Details</span>
            </div>
            <div class="section-body">
              <div class="detail-row" @click="copyContent(companyDetails.oriCompanyName)">
                <span class="detail-label">Company Name</span>
                <span class="detail-val">{{ companyDetails.oriCompanyName }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
              <div class="detail-row" @click="copyContent(companyDetails.companyAddress)">
                <span class="detail-label">Company Address</span>
                <span class="detail-val">{{ companyDetails.companyAddress }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
              <div class="detail-row" @click="copyContent(companyDetails.companySsmNo)">
                <span class="detail-label">Company SSM No.</span>
                <span class="detail-val">{{ companyDetails.companySsmNo }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
              <div class="detail-row" @click="copyContent(companyDetails.companyTinNo)">
                <span class="detail-label">Company TIN No.</span>
                <span class="detail-val">{{ companyDetails.companyTinNo }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>

              <div class="section-divider"></div>

              <div class="detail-row" @click="copyContent(companyDetails.picName)">
                <span class="detail-label">Client PIC Name</span>
                <span class="detail-val"><span v-html="companyDetails.picNameDisplay"></span>
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
              <div class="detail-row" @click="copyContent(companyDetails.picEmailTo)">
                <span class="detail-label">Client PIC Email (To)</span>
                <span class="detail-val detail-val--lowercase"><span v-html="companyDetails.picEmailToDisplay || '-'"></span>
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
              <div class="detail-row" @click="copyContent(companyDetails.picEmailCc)">
                <span class="detail-label">Client PIC Email (Cc)</span>
                <span class="detail-val detail-val--lowercase"><span v-html="companyDetails.picEmailCcDisplay || '-'"></span>
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
              <div class="detail-row" @click="copyContent(companyDetails.picContactNo)">
                <span class="detail-label">Client PIC Contact No</span>
                <span class="detail-val"><span v-html="companyDetails.picContactNoDisplay"></span>
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>

              <div class="section-divider"></div>

              <div class="detail-row" @click="copyContent(companyDetails.bankName)">
                <span class="detail-label">Bank Name</span>
                <span class="detail-val">{{ companyDetails.bankName }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
              <div class="detail-row" @click="copyContent(companyDetails.bankAccNo)">
                <span class="detail-label">Bank Account No</span>
                <span class="detail-val">{{ companyDetails.bankAccNo }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
              <div class="detail-row" @click="copyContent(companyDetails.bankAccHolderName)">
                <span class="detail-label">Bank Account Holder</span>
                <span class="detail-val">{{ companyDetails.bankAccHolderName }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
            </div>
          </div>

          <!-- Salesperson Details -->
          <div class="section-card">
            <div class="section-header">
              <v-icon color="white" small>mdi-briefcase-account</v-icon>
              <span class="section-header__title">Salesperson Details</span>
            </div>
            <div class="section-body">
              <div class="detail-row" @click="copyContent(salespersonDetails.salespersonName)">
                <span class="detail-label">Salesperson Name</span>
                <span class="detail-val">{{ salespersonDetails.salespersonName }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
              <div class="detail-row" @click="copyContent(salespersonDetails.salespersonEmail)">
                <span class="detail-label">Salesperson Email</span>
                <span class="detail-val">{{ salespersonDetails.salespersonEmail }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>

              <div class="section-divider"></div>

              <div class="detail-row" @click="copyContent(customerServiceDetails.customerServiceName)">
                <span class="detail-label">Customer Service</span>
                <span class="detail-val">{{ customerServiceDetails.customerServiceName }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
            </div>
          </div>

          <!-- Attachment Details -->
          <div class="section-card">
            <div class="section-header">
              <v-icon color="white" small>mdi-attachment</v-icon>
              <span class="section-header__title">Attachment Details</span>
            </div>
            <div class="section-body">
              <div
                v-if="attachmentTreeview.length == 0"
                class="empty-state"
              >
                No Attachment Available
              </div>
              <v-treeview
                dense
                hoverable
                open-all
                :items="attachmentTreeview"
                :transition="true"
              >
                <template v-slot:prepend="{ item, open, leaf }">
                  <div
                    class="atch-row"
                    :class="{ 'atch-row--leaf': leaf }"
                    @contextmenu="leaf ? showFileActionList($event, item) : null"
                    @dblclick="leaf ? previewOrDownloadAttachment(item, 'preview') : null"
                  >
                    <v-icon v-if="!leaf" color="#f2b720" class="atch-icon">
                      {{ open ? "mdi-folder-open" : "mdi-folder" }}
                    </v-icon>
                    <v-icon
                      v-else
                      :color="fileExtension[item.fileExtension] ? fileExtension[item.fileExtension].color : 'grey'"
                      class="atch-icon"
                    >
                      {{ fileExtension[item.fileExtension] ? fileExtension[item.fileExtension].icon : 'mdi-file-outline' }}
                    </v-icon>
                    <span class="atch-name">
                      {{ item.fileName }}
                    </span>
                  </div>
                </template>
                <template v-slot:label="{ item, leaf }">
                  <div
                    v-if="leaf"
                    class="atch-label-fill"
                    @contextmenu="showFileActionList($event, item)"
                    @dblclick="previewOrDownloadAttachment(item, 'preview')"
                  >&nbsp;</div>
                  <span v-else>{{ item.name }}</span>
                </template>
              </v-treeview>
            </div>
          </div>
        </div>

        <!-- RIGHT COLUMN -->
        <div class="cards-column">
          <!-- Application Details -->
          <div class="section-card">
            <div class="section-header">
              <v-icon color="white" small>mdi-application-edit-outline</v-icon>
              <span class="section-header__title">Application Details</span>
            </div>
            <div class="section-body">
              <div class="detail-row">
                <span class="detail-label">Current Stage</span>
                <span class="stage-badges" v-html="applicationDetails.stageName"></span>
              </div>

              <div class="section-divider"></div>

              <div class="detail-row" @click="copyContent(applicationDetails.eventTitle)">
                <span class="detail-label">Event Title / Programme</span>
                <span class="detail-val">{{ applicationDetails.eventTitle }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
              <div class="detail-row" @click="copyContent(eventPeriod)">
                <span class="detail-label">Event Date</span>
                <span class="detail-val">{{ eventPeriod }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
              <div class="detail-row" @click="copyContent(String(applicationDetails.noOfPax))">
                <span class="detail-label">No. of Pax</span>
                <span class="detail-val">{{ applicationDetails.noOfPax }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>

              <div class="section-divider"></div>

              <!-- Event Participants -->
              <div class="participant-slider" v-if="applicationDetails.eventParticipant && applicationDetails.eventParticipant.length > 0">
                <div class="participant-slider__header">
                  <span class="participant-slider__badge">Participant {{ eventParticipantCarousel + 1 }} of {{ applicationDetails.eventParticipant.length }}</span>
                  <div class="participant-slider__nav">
                    <button class="participant-slider__btn" :disabled="eventParticipantCarousel === 0" @click="eventParticipantCarousel--">
                      <v-icon size="18">mdi-chevron-left</v-icon>
                    </button>
                    <button class="participant-slider__btn" :disabled="eventParticipantCarousel === applicationDetails.eventParticipant.length - 1" @click="eventParticipantCarousel++">
                      <v-icon size="18">mdi-chevron-right</v-icon>
                    </button>
                  </div>
                </div>
                <div class="detail-row" @click="copyContent(applicationDetails.eventParticipant[eventParticipantCarousel].participantName)">
                  <span class="detail-label">Name</span>
                  <span class="detail-val">{{ applicationDetails.eventParticipant[eventParticipantCarousel].participantName }}
                    <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                  </span>
                </div>
                <div class="detail-row" @click="copyContent(applicationDetails.eventParticipant[eventParticipantCarousel].participantNric)">
                  <span class="detail-label">IC No.</span>
                  <span class="detail-val">{{ applicationDetails.eventParticipant[eventParticipantCarousel].participantNric }}
                    <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                  </span>
                </div>
                <div class="detail-row" @click="copyContent(applicationDetails.eventParticipant[eventParticipantCarousel].participantEmail)">
                  <span class="detail-label">Email</span>
                  <span class="detail-val">{{ applicationDetails.eventParticipant[eventParticipantCarousel].participantEmail }}
                    <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                  </span>
                </div>
              </div>

              <div class="section-divider"></div>

              <div class="detail-row" @click="copyContent(applicationDetails.paymentChannel)">
                <span class="detail-label">Payment Channel</span>
                <span class="detail-val">{{ applicationDetails.paymentChannel }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
              <div class="detail-row" @click="copyContent(applicationDetails.paymentMode)">
                <span class="detail-label">Payment Mode</span>
                <span class="detail-val">{{ applicationDetails.paymentMode }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
              <div class="detail-row" @click="copyContent(applicationDetails.refundType)">
                <span class="detail-label">Refund Type</span>
                <span class="detail-val">{{ applicationDetails.refundType }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>

              <div class="section-divider"></div>

              <div class="detail-row">
                <span class="detail-label">Payment Amount</span>
                <span>
                  <span class="badge badge--green">
                    {{ applicationDetails.paymentAmount }}
                  </span>
                </span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Claim Amount</span>
                <span>
                  <span class="badge badge--amber">
                    {{ applicationDetails.totalPrice }}
                  </span>
                </span>
              </div>
            </div>
          </div>

          <!-- Statement Details -->
          <div class="section-card">
            <div class="section-header">
              <v-icon color="white" small>mdi-folder-information</v-icon>
              <span class="section-header__title">Statement Details</span>
            </div>
            <div class="section-body">
              <div
                class="detail-row"
                v-for="(statement, index) in formattedStatementDetails"
                :key="'formattedStatementDetails' + index"
                @click="copyContent(statement)"
              >
                <span class="detail-label">{{ index }}</span>
                <span class="detail-val detail-val--lowercase">{{ statement }}
                  <v-icon x-small class="copy-icon">mdi-content-copy</v-icon>
                </span>
              </div>
            </div>
          </div>

          <!-- Other Details -->
          <div class="section-card">
            <div class="section-header">
              <v-icon color="white" small>mdi-dots-horizontal-circle-outline</v-icon>
              <span class="section-header__title">Other Details</span>
            </div>
            <div class="section-body">
              <h4 class="remarks-title">Remarks</h4>
              <div v-if="checkRemarks" class="remarks-table-wrap">
                <table class="remarks-table">
                  <thead>
                    <tr>
                      <th>Stage</th>
                      <th>Remark</th>
                      <th>By / Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="remark in otherDetails.remarks"
                      :key="remark.id"
                      :class="{
                        'remarks-row--general': remark.id === 'GENERAL_REMARKS',
                      }"
                    >
                      <td>{{ remark.stageName }}</td>
                      <td>{{ remark.remarks }}</td>
                      <td>{{ remark.remarkBy }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="empty-state">
                No remarks available
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- General Remarks FAB -->
      <button class="remarks-fab" @click="generalRemarksDialog = true">
        <v-icon size="22" color="#fff">mdi-note-edit-outline</v-icon>
      </button>
    </div>

    <!-- Page Loading State -->
    <div v-if="loadingDialog" class="page-loader">
      <div class="page-loader__skeleton">
        <!-- Header skeleton -->
        <div class="skel-header">
          <div class="skel-line skel-line--lg"></div>
          <div class="skel-line skel-line--sm"></div>
          <div class="skel-bar"></div>
        </div>
        <!-- Cards skeleton -->
        <div class="skel-grid">
          <div class="skel-card" v-for="n in 6" :key="n">
            <div class="skel-card__header"></div>
            <div class="skel-card__body">
              <div class="skel-line skel-line--md"></div>
              <div class="skel-line skel-line--full"></div>
              <div class="skel-line skel-line--lg"></div>
              <div class="skel-line skel-line--md"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <HrdcLoader :loadingDialog="atchLoadingDialog"></HrdcLoader>
  </v-container>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import HrdcData from "@/services/hrdc_event/objects/globalData";
import TheModalConfirmation from "@/components/common/TheModalConfirmation.vue";
import EmailReminderDialog from "@/services/hrdc_event/views/settings/email_reminder/EmailReminderDialog.vue";

export default {
  components: {
    TheModalConfirmation,
    EmailReminderDialog,
  },
  mixins: [hrdcFunction],
  computed: mapState({
    auth: (state) => state.auth.data,
    formattedStatementDetails() {
      const formatted = {};
      for (const key in this.statementDetails) {
        switch (key) {
          case "manualInvoiceToHrdcNo":
            if (this.applicationDetails._refundType == 2) {
              formatted["Manual Invoice to HRDC (70%)"] =
                this.statementDetails[key];
              break;
            }

            formatted[this.camelCaseToNormalCase(key)] =
              this.statementDetails[key];
            break;
          case "manualInvoiceToHrdcNo2":
            if (this.applicationDetails._refundType == 2) {
              formatted["Manual Invoice to HRDC (30%)"] =
                this.statementDetails[key];
              break;
            }
            break;
          default:
            formatted[this.camelCaseToNormalCase(key)] =
              this.statementDetails[key];
            break;
        }
      }
      return formatted;
    },
    eventPeriod() {
      if (
        this.applicationDetails.startDate ||
        this.applicationDetails.endDate
      ) {
        const eventStartDate = moment(this.applicationDetails.startDate).format(
          "DD/MM/YYYY"
        );
        const eventEndDate = moment(this.applicationDetails.endDate).format(
          "DD/MM/YYYY"
        );

        let eventDate = eventStartDate;
        if (eventEndDate !== "Invalid date") {
          eventDate += `  to ${eventEndDate}`;
        }
        return eventDate;
      }

      return "-";
    },
    checkRemarks() {
      if (this.otherDetails.remarks.length > 0) {
        return true;
      }

      return false;
    },
    flaggedCase() {
      let color = null;
      let mdiIcon = "mdi-flag-outline";
      this.alertError = this.alertError.filter(
        (error) => error !== "This case is being flagged"
      );
      if (this.applicationDetails.isFlag) {
        color = "error";
        mdiIcon = "mdi-flag";

        this.alertError.push("This case is being flagged");
        this.alertErrorIcon = "mdi-flag";
      }

      return {
        color,
        mdiIcon,
      };
    },
    displayDeletedApplication() {
      if (this.applicationDetails.isApplicationDeleted) {
        return false;
      }

      return true;
    },
  }),
  props: ["params"],
  data: () => ({
    apiGetApplicationDetails: new Api(),
    apiDownloadOrPreviewAttachment: new Api(),
    apiGetApplicationStages: new Api(),
    apiGetEmailDetails: new Api(),
    apiGetAttachmentBlob: new Api(),
    apiDeleteApplication: new Api(),
    apiRestoreApplication: new Api(),
    apiSubmitGeneralRemarks: new Api(),
    apiDeleteAttachment: new Api(),
    apiUpdateApplicationStages: new Api(),
    apiSubmit: new Api(),
    api: new Api(),
    model: new Model(),
    hrdcData: new HrdcData(),
    service: new Service(),
    emailReminderDialog: false,
    applicationLogs: [],
    generalLogs: [],
    stages: [],
    allStages: [],
    loadingDialog: false,
    loadingDialogBar: false,
    panel: [0, 1, 2, 3, 4, 5, 6, 7],
    errorField: {
      updateStageId: null,
      customerServiceSelect: null,
      flagRemarks: null,
    },
    companyDetails: {
      oriCompanyName: null,
      companyName: null,
      companyAddress: null,
      companySsmNo: null,
      companyTinNo: null,
      picName: null,
      picContactNo: null,
      picEmailTo: null,
      picEmailCc: null,
      bankName: null,
      bankAccNo: null,
      bankAccHolderName: null,
      oldCompanyId: null,
      picNameDisplay: "",
      picEmailToDisplay: "",
      picEmailCcDisplay: "",
      picContactNoDisplay: "",
    },
    salespersonDetails: {
      salespersonName: null,
      salespersonEmail: null,
      salespersonContactNo: null,
    },
    customerServiceDetails: {
      customerServiceName: "-",
      customerServiceSelect: [],
    },
    applicationDetails: {
      stageId: null,
      updateStageId: null,
      eventTitle: null,
      startDate: null,
      endDate: null,
      noOfPax: null,
      eventParticipant: null,
      stageName: null,
      oriPrice: null,
      subtotalPrice: null,
      totalPrice: null,
      paymentAmount: null,
      oriTotalPrice: null,
      taxPrice: null,
      taxRate: null,
      isPaymentMade: null,
      paymentChannel: null,
      toRefund: null,
      paymentMode: null,
      refundType: null,
      _refundType: null,
      isFlag: null,
      isApplicationDeleted: false,
    },
    attachmentDetails: [],
    generalAttachment: [],
    hrdcAttachment: [],
    financeAttachment: [],
    othersAttachment: [],
    attachmentTreeview: [
      {
        id: 1,
        name: "HRDC",
        children: null,
      },
      {
        id: 2,
        name: "General",
        children: null,
      },
      {
        id: 3,
        name: "Finance",
        children: null,
      },
      {
        id: 4,
        name: "Others",
        children: null,
      },
    ],
    statementDetails: {
      grantApprovalNo: "-",
      employerCodeEtris: "-",
      manualInvoiceToHrdcNo: "-",
      manualInvoiceToHrdcNo2: "-",
      claimId: "-",
      creditNote: "-",
      autoGeneratedInvoice: "-",
    },
    otherDetails: {
      remarks: [],
      generalRemarks: null,
      flagRemarks: null,
    },
    emailModalDetails: {
      emailModalHeader: null,
      emailModalSubject: null,
      emailModalContent: null,
      emailModalToRecipient: null,
      emailModalCcRecipient: null,
    },
    updateStatusBtn: null,
    updateStatusText: null,
    rolesAllowed: null,
    updateStatusDialog: false,
    deleteApplicationDialog: false,
    generalRemarksDialog: false,
    actions: null,
    stageCountable: [],
    customerServiceItems: [],
    csDropdownOpen: false,
    alertError: [],
    alertErrorIcon: "mdi-alert",
    duplicateApplicationAlertError: [],
    direction: "top",
    fab: false,
    tabs: null,
    right: true,
    bottom: true,
    transition: "slide-y-reverse-transition",
    nextActionDisplay: false,
    emailDialog: false,
    viewLogDialog: false,
    restoreApplicationDialog: false,
    alertColor: null,
    flagDialog: false,
    flagRemarks: null,
    flagOrUnflagText: "Flag",
    eventParticipantCarousel: 0,
    logTab: "tab-1",
    showAttachmentAction: {
      menu: false,
      view: false,
      download: false,
      email: false,
    },
    attachmentX: 0,
    attachmentY: 0,
    attachmentPointingItem: null,
    fileExtension: {
      pdf: {
        icon: "mdi-file-document-outline",
        color: "red lighten-2",
      },
      png: {
        icon: "mdi-image",
        color: "primary",
      },
      jpg: {
        icon: "mdi-image",
        color: "primary",
      },
      jpeg: {
        icon: "mdi-image",
        color: "primary",
      },
      email: {
        icon: "mdi-email-outline",
        color: "green",
      },
      xlsx: {
        icon: "mdi-microsoft-excel",
        color: "green darken-2",
      },
    },
    atchLoadingDialog: false,
    deleteAttachmentDialog: false,
    deleteAttachmentItem: null,
  }),
  async created() {
    this.loadingDialog = !this.loadingDialog;
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);
    this.allStages = await this.hrdcData.allStages;
    this.stageCountable.push(
      this.hrdcData.stagePriority.quotationDateScheduled,
      this.hrdcData.stagePriority.subscriptionDateScheduled,
      this.hrdcData.stagePriority.sentJd14FormToClient,
      this.hrdcData.stagePriority.sentT3FormToClient,
      this.hrdcData.stagePriority.sentConfirmationEmailToClient
    );

    this.rolesAssignation(this.auth.roles);
    this.serviceDataAssignation(this.model.serviceKey);
    this.alertColor = this.HRDC_BLUE_THEME_COLOR;

    // Set actions.
    this.actions = [...this.INDEX_MENU_ACTIONS];

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiGetApplicationDetails: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-application-details`,
        method: "post",
        params: {
          applicationUuid: this.$route.query._ausk,
          type: "MULTIPLE_ENROLLMENT_DETAILS",
        },
      },
      apiDownloadOrPreviewAttachment: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/azure-download-or-preview-application-attachment`,
      },
      apiGetApplicationStages: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-application-stages`,
        method: "post",
        params: {
          applicationUuid: this.$route.query._ausk,
        },
      },
      apiGetEmailDetails: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-email-details`,
        method: "post",
      },
      apiGetAttachmentBlob: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-attachment-blob`,
      },
      apiDeleteApplication: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/delete-application`,
        method: "delete",
      },
      apiRestoreApplication: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/restore-application`,
        method: "post",
      },
      apiSubmitGeneralRemarks: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/general_info/create-general-remarks`,
        method: "post",
      },
      apiDeleteAttachment: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/delete-attachment`,
        method: "delete",
      },
      apiUpdateApplicationStages: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/update-application-stages`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.apiGetApplicationDetails.setCallbackCompleted(async (response) => {
      try {
        const { data } = await response;

        const { applicationLog } = response;

        if (!response.status) {
          this.$store.commit("assignDeveloperData", {
            paginationPageNo: this.$route.query.currentPageNo,
          });

          this.$router.push({
            name: this.eventRoute("Application"),
            query: {
              viewId: this.$store.state[this.service.key].data.viewId,
            },
          });
          this.$store.dispatch("showMessage", {
            message: data,
            messageType: "error",
            timeout: 2000,
          });
          return;
        }

        // To alert admin if detected similar company name.
        const similarCompanyName = response.companyNamesArray;
        if (similarCompanyName) {
          for (const sCompany of similarCompanyName) {
            this.duplicateApplicationAlertError.push({
              title: sCompany.companyName,
              value: sCompany,
            });
          }
        }

        // Company details.
        this.companyDetails.oriCompanyName = data.oriCompanyName.toUpperCase();
        // this.companyDetails.companyNameOnly = data.oldCompanyName.toUpperCase();
        this.companyDetails.companyName = `${data.oriCompanyName.toUpperCase()} (${
          data.Company[0].oldSsmNo ?? "No SSM No."
        })`;
        this.companyDetails.companyAddress =
          data.Company[0].oldAddress.toUpperCase() ?? "-";
        this.companyDetails.companySsmNo =
          data.Company[0].oldSsmNo ?? "No SSM No.";
        this.companyDetails.companyTinNo =
          data.Company[0].companyTinNo ?? "-";

        const picNameDisplay = response.companyPicData
          .map((item) => item.picName)
          .filter((item) => item !== null);

        const picName = picNameDisplay.join(",");

        for (const name of picNameDisplay) {
          this.companyDetails.picNameDisplay += `${name} <br/>`;
        }

        // Split PIC emails by recipientType. Legacy rows (recipientType null)
        // are treated as TO so existing applications keep displaying sensibly.
        const picEmailToList = response.companyPicData
          .filter(
            (item) =>
              item.picEmail &&
              (item.recipientType === "TO" || item.recipientType == null)
          )
          .map((item) => item.picEmail);
        const picEmailCcList = response.companyPicData
          .filter((item) => item.picEmail && item.recipientType === "CC")
          .map((item) => item.picEmail);

        for (const email of picEmailToList) {
          this.companyDetails.picEmailToDisplay += `${email} <br/>`;
        }
        for (const email of picEmailCcList) {
          this.companyDetails.picEmailCcDisplay += `${email} <br/>`;
        }

        const picContactNoDisplay = response.companyPicData
          .map((item) => item.picContactNo)
          .filter((item) => item !== null);

        const picContactNo = picContactNoDisplay.join(",");

        for (const contactNo of picContactNoDisplay) {
          this.companyDetails.picContactNoDisplay += `${contactNo} <br/>`;
        }

        this.companyDetails.picName = picName ? picName : "-";
        this.companyDetails.picEmailTo = picEmailToList.length
          ? picEmailToList.join(",")
          : "-";
        this.companyDetails.picEmailCc = picEmailCcList.length
          ? picEmailCcList.join(",")
          : "-";
        this.companyDetails.picContactNo = picContactNo ? picContactNo : "-";
        this.companyDetails.bankName = data.bankUuid || "-";
        this.companyDetails.bankAccNo = data.oldBankAccount || "-";
        this.companyDetails.bankAccHolderName =
          data.oldBankAccountHolderName || "-";

        if (data.paymentChannel == "2") {
          this.companyDetails.bankName = "-";
          this.companyDetails.bankAccNo = "-";
          this.companyDetails.bankAccHolderName = "-";
        }

        this.companyDetails.oldCompanyId = data.oldCompanyId;

        // Salesperson details.
        this.salespersonDetails.salespersonName = data.salesperson.name ?? "-";
        this.salespersonDetails.salespersonEmail =
          data.salesperson.email ?? "-";
        this.salespersonDetails.salespersonContactNo =
          data.salesperson.mobile ?? "-";

        // Customer Service details.
        if (response.applicationCs.length > 0) {
          for (const cs of response.applicationCs) {
            this.customerServiceDetails.customerServiceSelect.push(cs.csUuid);
            if ("csName" in cs) {
              this.customerServiceDetails.customerServiceName =
                response.applicationCs
                  .filter((item) => item.csName && item.csName !== "")
                  .map((item) => item.csName)
                  .join(",");
              break;
            }
          }
        }

        if (response.csData) {
          for (const cs of response.csData) {
            this.customerServiceItems.push({
              text: cs.email,
              value: cs.uuid,
            });
          }
        }

        // Application details.
        this.applicationDetails.stageId = data.stageId;
        this.applicationDetails.eventTitle = data.Event.title;
        this.applicationDetails.startDate =
          data.Event.startDate && data.Event.startDate !== "Invalid date"
            ? data.Event.startDate
            : null;
        this.applicationDetails.endDate =
          data.Event.endDate && data.Event.endDate !== "Invalid date"
            ? data.Event.endDate
            : null;

        this.applicationDetails.noOfPax = data.noOfPax;
        this.applicationDetails.eventParticipant =
          data.ApplicationEventParticipant;

        const stageNameSlug = Object.entries(this.hrdcData.stagePriority).find(
          ([, value]) => value === data.stageId
        )?.[0];

        const stageName = this.hrdcData.stageSlugToStageName[stageNameSlug];
        const applicationAttachment = data.ApplicationAttachment;

        this.applicationDetails.stageName = this.castStageName({
          stageName,
          stageNameSlug,
          applicationLog,
          applicationAttachment,
          grantApprovalNo: data.grantApprovalNo,
        });

        this.applicationDetails.oriPrice = data.oriPrice;
        this.applicationDetails.subtotalPrice = `RM ${this.formatNumber(
          data.subtotalPrice
        )}`;
        this.applicationDetails.totalPrice = `RM ${this.formatNumber(
          data.totalPrice
        )}`;
        this.applicationDetails.paymentAmount = data.isPaymentMade
          ? `RM ${this.formatNumber(data.paymentAmount)}`
          : "-";
        this.applicationDetails.oriTotalPrice = data.totalPrice;
        this.applicationDetails.taxPrice = `RM ${this.formatNumber(
          data.taxPrice
        )}`;

        this.applicationDetails.taxRate = data.taxRate;
        this.applicationDetails.isPaymentMade = data.isPaymentMade;
        this.applicationDetails.paymentChannel = data.isPaymentMade
          ? "Pay & Refund"
          : "Claim Basis";

        this.applicationDetails.paymentMode =
          data.paymentMode !== null
            ? data.paymentMode +
              (data.paymentMode == "Other" ? ` - ${data.otherPaymentMode}` : ``)
            : "-";

        // this.applicationDetails.programmeName = data.programmeName;
        this.applicationDetails._refundType = data.refundType;
        if (data.refundType == 1) {
          this.applicationDetails.refundType = "Normal";
        } else {
          this.applicationDetails.refundType = `70% (RM ${Number(
            data.seventyPercentPrice
          ).toFixed(2)}) / 30% (RM ${Number(data.thirtyPercentPrice).toFixed(
            2
          )})`;
        }

        this.applicationDetails.isFlag = data.isFlag;
        this.applicationDetails.isApplicationDeleted = data.timestampDeleted;

        // Statement details.
        this.statementDetails.grantApprovalNo = data.grantApprovalNo ?? "-";
        this.statementDetails.employerCodeEtris = data.employerCodeEtris ?? "-";
        this.statementDetails.manualInvoiceToHrdcNo =
          data.manualInvoiceToHrdc ?? "-";
        this.statementDetails.manualInvoiceToHrdcNo2 =
          data.manualInvoiceToHrdc2 ?? "-";
        this.statementDetails.claimId = data.claimId ?? "-";
        this.statementDetails.creditNote = data.creditNoteNo ?? "-";
        this.statementDetails.autoGeneratedInvoice =
          data.autoGeneratedClientInvoiceNo &&
          data.autoGeneratedClientInvoiceNo !== ""
            ? data.autoGeneratedClientInvoiceNo
            : "-";
        this.statementDetails.stripeRef = data.stripeRef ?? "-";

        const formatted = {};
        for (const key in this.statementDetails) {
          switch (key) {
            case "manualInvoiceToHrdcNo":
              if (this.applicationDetails._refundType == 2) {
                formatted["Manual Invoice to HRDC (70%)"] =
                  this.statementDetails[key];
                break;
              }

              formatted[this.camelCaseToNormalCase(key)] =
                this.statementDetails[key];
              break;
            case "manualInvoiceToHrdcNo2":
              if (this.applicationDetails._refundType == 2) {
                formatted["Manual Invoice to HRDC (30%)"] =
                  this.statementDetails[key];
                break;
              }
              break;
            default:
              formatted[this.camelCaseToNormalCase(key)] =
                this.statementDetails[key];
              break;
          }
        }

        // Attachment details.
        let paymentAdviceCount = 1;
        let clientInvoiceCount = 1;
        let creditNoteCount = 1;

        const countOfPaymentAdviceToClient = data.ApplicationAttachment.filter(
          (item) =>
            item.fileStageId ==
            Number(this.hrdcData.stagePriority.emailPaymentAdviceToClient)
        );

        const countOfClientInvoice = data.ApplicationAttachment.filter(
          (item) =>
            item.fileStageId ==
            Number(this.hrdcData.stagePriority.autoGeneratedTaxpodInvoice)
        );

        for (
          let index = 0;
          index < data.ApplicationAttachment.length;
          index++
        ) {
          const item = data.ApplicationAttachment[index];

          let fileName = item.fileName;
          let fileNameSlug = this.titleCaseToUppercase(item.fileName);
          let downloadFileName = null;

          if (
            [
              this.hrdcData.stageSlug.sentJd14FormToClient,
              this.hrdcData.stageSlug.sentT3FormToClient,
              this.hrdcData.stageSlug.sentConfirmationEmailToClient,
            ].includes(this.hrdcData.stageIndex[item.fileStageId])
          ) {
            // Set different file name if is send email to client.
            fileName = `${item.fileName} @ ${moment(
              item.fileTimestampCreated
            ).format("YYYY-MM-DD")}`;
          }

          switch (fileNameSlug) {
            case "APPLICATION_SUBMITTED":
              if (data.paymentChannel == 2) {
                continue; // Continue the loop if Claim Basis (no screenshot require)
              }
              fileName = "Screenshot of Bank Account Details for Refund";
              break;
            case "MANUAL_INVOICE_TO_HRDC":
              if (data.refundType == 2) {
                const manualInvoiceExists = this.attachmentDetails.some(
                  (item) => item.fileNameSlug == fileNameSlug
                );
                if (manualInvoiceExists) {
                  fileName = "HRDC Invoice (30%)";
                } else {
                  fileName = "HRDC Invoice (70%)";
                }
              } else {
                fileName = "HRDC Invoice";
              }

              downloadFileName = this.statementDetails.manualInvoiceToHrdcNo;
              break;
            case "CLIENT_REVERT_JD14_FORM":
              fileName = "JD14 Completed";
              downloadFileName = "JD14";
              break;
            case "CLIENT_REVERT_T3_FORM":
              fileName = "T3 Completed";
              downloadFileName = "T3";
              break;
            case "REFUND_EMAIL_AR_TO_AP":
              fileName = "Refund Email AR to AP (Compiled PDF)";
              break;

            case "EMAIL_PAYMENT_ADVICE_TO_CLIENT":
              if (countOfPaymentAdviceToClient.length == 1) {
                fileName = "Payment Advice to Client";
              } else {
                fileName = `Payment Advice to Client - ${paymentAdviceCount}`;
                paymentAdviceCount++;
              }
              break;

            case "AUTO_GENERATED_TAXPOD_INVOICE":
              if (countOfClientInvoice.length == 1) {
                fileName = "Client Invoice";
              } else {
                fileName = `Client Invoice - ${clientInvoiceCount}`;
                clientInvoiceCount++;
              }
              break;

            case "CREDIT_NOTE_GENERATED":
              if (countOfClientInvoice.length == 1) {
                fileName = "Credit Note";
              } else {
                fileName = `Credit Note - ${creditNoteCount}`;
                creditNoteCount++;
              }
              break;
          }

          let tmpAttachmentObj = {
            uuid: item.uuid,
            attachmentUuid: item.attachmentUuid,
            fileName,
            fileNameSlug,
            fileUrl: item.fileUrl,
            applicationUuid: item.applicationUuid,
            fileStageId: item.fileStageId,
            azureStorage: true,
            showView: true,
            showDownload: true,
            downloadFileName,
            type: null,
            fileExtension: item.fileUrl
              ? this.getFileExtension(item.fileUrl)
              : null,
          };

          if (
            tmpAttachmentObj.fileName !== null &&
            tmpAttachmentObj.fileUrl !== null
          ) {
            this.attachmentDetails.push(tmpAttachmentObj);

            let attachmentDuplicateCheck = null;

            switch (item.fileStageId) {
              // Admin related docs.
              case this.hrdcData.stagePriority.applicationSubmitted:
              case this.hrdcData.stagePriority.manualInvoiceToHrdc:
              case this.hrdcData.stagePriority.autoGeneratedTaxpodInvoice:
              case this.hrdcData.stagePriority.paymentAdviceFromHrdc:
              case this.hrdcData.stagePriority.emailPaymentAdviceToClient:
              case this.hrdcData.stagePriority.paymentAdviceFromClient:
                tmpAttachmentObj.type = "General";
                this.generalAttachment.push(tmpAttachmentObj);
                break;

              // HRDC related docs.
              case this.hrdcData.stagePriority.sentConfirmationEmailToClient:
              case this.hrdcData.stagePriority.sentJd14FormToClient:
              case this.hrdcData.stagePriority.sentT3FormToClient:
              case this.hrdcData.stagePriority.clientRevertJd14Form:
              case this.hrdcData.stagePriority.clientRevertT3Form:
              case this.hrdcData.stagePriority.hrdcGrantEmail:
              case this.hrdcData.stagePriority.attendanceForm:
              case this.hrdcData.stagePriority.attendanceList:
                tmpAttachmentObj.type = "HRDC";
                this.hrdcAttachment.push(tmpAttachmentObj);
                break;

              // Finance related docs.
              case this.hrdcData.stagePriority.creditNote:
              case this.hrdcData.stagePriority.creditNoteGenerated:
              case this.hrdcData.stagePriority.paymentVoucher:
                tmpAttachmentObj.type = "Finance";
                this.financeAttachment.push(tmpAttachmentObj);
                break;

              case this.hrdcData.stagePriority.refundEmailArToAp:
                attachmentDuplicateCheck = this.financeAttachment.find(
                  (item) => {
                    return (
                      item.showView &&
                      item.fileStageId == tmpAttachmentObj.fileStageId
                    );
                  }
                );

                if (attachmentDuplicateCheck) {
                  this.financeAttachment = this.financeAttachment.filter(
                    (item) =>
                      item.attachmentUuid !==
                      attachmentDuplicateCheck.attachmentUuid
                  );
                }
                tmpAttachmentObj.type = "Finance";
                this.financeAttachment.push(tmpAttachmentObj);
                break;

              // Others related docs.
              default:
                tmpAttachmentObj.type = "Others";
                this.othersAttachment.push(tmpAttachmentObj);
                break;
            }
          }
        }

        // Add Refund email.
        if (
          this.hrdcData.stageIndex[
            this.$store.state[this.service.key].data.stageId
          ] >= this.hrdcData.stageSlug.refundEmailSubmission &&
          this.applicationDetails.paymentChannel !== "Claim Basis"
        ) {
          const refundEmailObj = {
            fileName: "Refund Email",
            fileNameSlug: null,
            fileUrl: null,
            applicationUuid: this.$route.query._ausk,
            fileStageId: this.hrdcData.stagePriority.refundEmailSubmission,
            azureStorage: true,
            showView: false,
            showDownload: false,
            showEmail: true,
            downloadFileName: null,
            fileExtension: "email",
          };

          this.attachmentDetails.push(refundEmailObj);
          this.generalAttachment.push(refundEmailObj);
        }

        // Add refund email ar to ap.
        if (
          this.hrdcData.stageIndex[
            this.$store.state[this.service.key].data.stageId
          ] >= this.hrdcData.stageSlug.refundEmailArToAp &&
          this.applicationDetails.paymentChannel !== "Claim Basis"
        ) {
          const refundEmailArToApObj = {
            fileName: "Refund Email AR to AP",
            fileNameSlug: null,
            fileUrl: null,
            applicationUuid: this.$route.query._ausk,
            fileStageId: this.hrdcData.stagePriority.refundEmailArToAp,
            azureStorage: true,
            showView: false,
            showDownload: false,
            showEmail: true,
            downloadFileName: null,
            fileExtension: "email",
          };

          this.attachmentDetails.push(refundEmailArToApObj);
          this.financeAttachment.push(refundEmailArToApObj);
        }

        // Categorize data into each diff module.
        const modulesToUpdate = ["HRDC", "General", "Finance", "Others"];
        const arraysToUpdate = [
          this.hrdcAttachment,
          this.generalAttachment,
          this.financeAttachment,
          this.othersAttachment,
        ];

        modulesToUpdate.forEach((name, index) => {
          const itemIndex = this.attachmentTreeview.findIndex(
            (item) => item.name === name
          );
          if (itemIndex !== -1) {
            this.attachmentTreeview[itemIndex].children = arraysToUpdate[index];

            if (this.attachmentTreeview[itemIndex].children.length === 0) {
              this.attachmentTreeview.splice(itemIndex, 1);
            }
          }
        });

        // Other Details.
        const generalRemarks = data.generalRemarks;
        this.otherDetails.generalRemarks = generalRemarks;

        // Flag Remarks Details.
        this.flagRemarks = data.flagRemarks;
        this.otherDetails.flagRemarks = this.flagRemarks;

        // Get Refund Email Remarks.
        const refundRemarks = data.refundRemarks;
        if (refundRemarks) {
          this.otherDetails.remarks.push({
            id: null,
            stageName: "Refund Email Remarks",
            remarks: refundRemarks,
            remarkBy: null,
          });
        }

        // Get logs, remarks details.
        this.apiGetApplicationStages.setCallbackCompleted((response) => {
          try {
            if (!response.status) {
              this.$store.dispatch("showMessage", {
                message: response.data,
                messageType: "error",
                timeout: 2000,
              });
            } else {
              let emailStageCount = {};
              const { data } = response;

              if (generalRemarks) {
                const lastUpdateGeneralRemarks = data.filter(
                  (item) =>
                    item.stageId ===
                    this.hrdcData.stagePriority.generalInfoRemarks
                );

                if (lastUpdateGeneralRemarks.length > 0) {
                  const lastElement = lastUpdateGeneralRemarks.pop();
                  this.otherDetails.remarks.push({
                    id: "GENERAL_REMARKS",
                    stageName: "General Remarks",
                    remarks: generalRemarks,
                    remarkBy: lastElement.performedBy,
                  });
                }
              }

              if (this.flagRemarks) {
                const lastUpdateFlagRemarks = data.filter(
                  (item) =>
                    item.stageId === this.hrdcData.stagePriority.normalFlag
                );

                if (lastUpdateFlagRemarks.length > 0) {
                  const lastFlagElement = lastUpdateFlagRemarks.pop();
                  this.otherDetails.remarks.push({
                    id: "FLAG_REMARKS",
                    stageName: "Flag Remarks",
                    remarks: this.flagRemarks,
                    remarkBy: lastFlagElement.performedBy,
                  });
                }
              }

              // Do not display refund remarks.
              const refundStage = [
                this.hrdcData.stagePriority.refundEmailSubmission,
                this.hrdcData.stagePriority.refundEmailApproval1,
                this.hrdcData.stagePriority.refundEmailApproval2,
              ];

              // Differentiate application logs & general logs.
              const generalLogStages = [
                this.hrdcData.stagePriority.generalInfoRemarks,
                this.hrdcData.stagePriority.normalFlag,
                this.hrdcData.stagePriority.normalUnflag,
              ];

              // Normal remarks from each forms.
              for (const item of data) {
                let stageName = item.stageName;

                // No need to display in log array.
                const undisplayStageLog = [
                  "Client Revert JD14 Form",
                  "Client Revert T3 Form",
                  "Quotation Date Scheduled",
                  "Sent JD14 Form To Client",
                  "Sent T3 Form To Client",
                  "Manual Invoice To HRDC",
                ];

                if (!undisplayStageLog.includes(stageName)) {
                  if (this.stageCountable.includes(item.stageId)) {
                    if (!emailStageCount[item.stageName]) {
                      emailStageCount[item.stageName] = 1;
                    } else {
                      emailStageCount[item.stageName]++;
                    }
                    stageName = `${stageName} - (Sent ${
                      emailStageCount[item.stageName]
                    } time)`;
                  }

                  if (generalLogStages.includes(item.stageId)) {
                    this.generalLogs.push({
                      id: item.id,
                      userUuid: item.performedBy ?? "-",
                      stageName: this.changeStageName(item.stageId, stageName),
                      createdDate: moment(item.timestampCreated).format(
                        "YYYY-MM-DD HH:mm"
                      ),
                      updatedDate: moment(item.timestampUpdated).format(
                        "YYYY-MM-DD HH:mm"
                      ),
                      startDate:
                        item.endDate !== null
                          ? moment(item.startDate).format("Y-MM-DD")
                          : "-",
                      endDate:
                        item.endDate !== null
                          ? moment(item.endDate).format("Y-MM-DD")
                          : "-",
                      scheduledDate: this.getScheduledDate(
                        item.stageId,
                        item.startDate,
                        item.endDate
                      ),
                    });
                  } else {
                    this.applicationLogs.push({
                      id: item.id,
                      userUuid: item.performedBy ?? "-",
                      stageName: this.changeStageName(item.stageId, stageName),
                      createdDate: moment(item.timestampCreated).format(
                        "YYYY-MM-DD HH:mm"
                      ),
                      updatedDate: moment(item.timestampUpdated).format(
                        "YYYY-MM-DD HH:mm"
                      ),
                      startDate:
                        item.endDate !== null
                          ? moment(item.startDate).format("Y-MM-DD")
                          : "-",
                      endDate:
                        item.endDate !== null
                          ? moment(item.endDate).format("Y-MM-DD")
                          : "-",
                      scheduledDate: this.getScheduledDate(
                        item.stageId,
                        item.startDate,
                        item.endDate
                      ),
                    });
                  }
                }

                if (item.remarks) {
                  if (!refundStage.includes(item.stageId)) {
                    let stageName = this.renameRemarkStageName(item.stageName);
                    if (!undisplayStageLog.includes(stageName)) {
                      this.otherDetails.remarks.push({
                        id: item.id,
                        stageName,
                        remarks: item.remarks,
                        remarkBy: item.performedBy,
                      });
                    }
                  }
                }
              }
            }
            this.loadingDialog = !this.loadingDialog;
          } catch (err) {
            console.log(err);
          }
        });
        this.apiGetApplicationStages.fetch();

        this.filterStatusByStagesAndConditions(
          this.hrdcData.stageIndex[this.applicationDetails.stageId]
        );

        const completeStage = [
          this.hrdcData.stagePriority.applicationDuplicated,
          this.hrdcData.stagePriority.applicationSubmitted,
          this.hrdcData.stagePriority.emailPaymentAdviceToClient,
          this.hrdcData.stagePriority.taxpodAccountActivation,
          this.hrdcData.stagePriority.hrdcClaimCompleted,
        ];

        this.nextActionDisplay = !completeStage.includes(
          this.applicationDetails.stageId
        )
          ? true
          : false;

        if (data.isPaymentMade) {
          this.actions.push(
            {
              title: "Refund Email Submission",
              route: this.eventRoute("RefundEmailSubmission"),
            },
            {
              title: "Refund Email Approval 1",
              route: this.eventRoute("RefundEmailApproval1"),
            },
            {
              title: "Refund Email Approval 2",
              route: this.eventRoute("RefundEmailApproval2"),
            },
            {
              title: "Refund Email AR to AP",
              route: this.eventRoute("RefundEmailArToAp"),
            },
            {
              title: "Email Payment Advice to Client",
              route: this.eventRoute("EmailPaymentAdviceToClient"),
            }
          );
        }

        if (this.checkRoleAllows("hrdc-super-admin")) {
          this.actions.push(
            // Append complete application function.
            {
              title: "Complete Application",
              route: "CompleteApplication",
            },
            // Append delete application function.
            {
              title: "Delete Application",
              route: "DeleteApplication",
            }
          );
        }
      } catch (err) {
        console.log(err);
      }
    });
    this.apiGetApplicationDetails.fetch();
  },
  methods: {
    getCustomerServiceText(val) {
      const item = this.customerServiceItems.find((i) => i.value === val);
      return item ? item.text : val;
    },
    toggleCustomerService(val) {
      const index = this.customerServiceDetails.customerServiceSelect.indexOf(val);
      if (index > -1) {
        this.customerServiceDetails.customerServiceSelect.splice(index, 1);
      } else {
        this.customerServiceDetails.customerServiceSelect.push(val);
      }
    },
    removeCustomerService(index) {
      this.customerServiceDetails.customerServiceSelect.splice(index, 1);
    },
    formatNumber(number) {
      if (number) {
        return Number(number).toLocaleString();
      }
    },
    restoreTrashApplication() {
      this.apiRestoreApplication.setParams({
        applicationUuid: this.$route.query._ausk,
      });
      this.apiRestoreApplication.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;
          if (!status) {
            this.$store.dispatch("showMessage", {
              message: data,
              messageType: "error",
              timeout: 2000,
            });
          }

          if (status) {
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });

            this.$router.push({
              name: this.eventRoute("ViewApplication"),
              query: {
                viewId: this.$store.state.hrdc.data.viewId,
                _ausk: this.$route.query._ausk,
              },
            });
          }
        } catch (err) {
          console.log(err);
          this.$store.dispatch("showMessage", {
            message: "Unable to delete application!",
            messageType: "error",
            timeout: 2000,
          });
        }
      });
      this.apiRestoreApplication.fetch();
    },
    showErrorMessage(response) {
      this.errorField.updateStageId = null;
      for (const key in response.data) {
        this.errorField[key] = response.data[key];
      }
    },
    updateApplicationStatus() {
      const fd = new FormData();
      fd.append("stageId", this.applicationDetails.updateStageId);
      fd.append("applicationUuid", this.$route.query._ausk);
      fd.append("userUuid", this.auth.uuid);
      fd.append("csUuid", this.customerServiceDetails.customerServiceSelect);

      fetch(
        `${
          this.$service[this.service.key]
        }/v1/en/console/application/update-stages`,
        {
          method: "post",
          body: fd,
          headers: {
            Authorization: "Bearer " + this.$store.state.auth.data.token,
          },
        }
      )
        .then((res) => res.json())
        .then((response) => {
          try {
            if (!response.status) {
              if (response.statusCode == 500) {
                this.$store.dispatch("showMessage", {
                  message: response.message,
                  messageType: "error",
                  timeout: 2000,
                });
                return;
              }
              this.showErrorMessage(response);
            } else {
              this.updateStatusDialog = !this.updateStatusDialog;
              this.$store.commit("assignDeveloperData", {
                paginationPageNo: this.$route.query.currentPageNo,
              });

              this.$router.push({
                name: this.eventRoute("Application"),
                query: {
                  viewId: this.$store.state[this.service.key].data.viewId,
                },
              });
              this.$store.dispatch("showMessage", {
                message: response.data,
                messageType: "success",
                timeout: 2000,
              });
            }
          } catch (err) {
            console.log(err);
          }
        });
    },
    modalUpdateApplicationStatus() {
      this.applicationDetails.updateStageId = this.updateStatusBtn;
      this.dialog = false;
      this.updateApplicationStatus();
    },
    filterStatusByStagesAndConditions(stagePriority) {
      const allStageSlug = this.hrdcData.stageSlug;
      const allStagePriority = this.hrdcData.stagePriority;
      for (const stage in allStageSlug) {
        switch (stagePriority) {
          case allStageSlug.applicationDuplicated:
          case allStageSlug.applicationSubmitted:
            // Stage=only allowed to approved this application
            if (stage == "applicationApproved") {
              let stageTmp = {
                text: this.camelCaseToNormalCase(stage),
                value: allStagePriority[stage],
              };

              this.updateStatusBtn = allStagePriority[stage];
              this.updateStatusText = stageTmp.text;
              this.rolesAllowed = this.checkRoleAllows(
                "hrdc-taxpod-application-approval-admin"
              );
              if (this.rolesAllowed) {
                this.alertColor = "#E57373";
                this.stages.push(stageTmp);
              }
            }
            break;
        }
      }
    },
    isImage(url) {
      return /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(url);
    },
    buildAttachmentShareLink(item) {
      // Permanent, copyable link. It points at our API, which redirects to a
      // fresh short-lived Azure SAS on each open — so the link itself never
      // expires while the underlying storage exposure stays short.
      return `${
        this.$service[this.service.key]
      }/v1/en/console/application/view-application-attachment?filePath=${encodeURIComponent(
        item.fileUrl
      )}`;
    },
    async copyAttachmentShareLink(item) {
      this.showAttachmentAction.menu = false;
      // Only Azure-stored attachments route through the backend share link.
      // Local flyers and external URLs have no shareable backend link.
      if (!item || !item.azureStorage || this.isValidURL(item.fileUrl)) {
        this.$store.dispatch("showMessage", {
          message: "This attachment has no shareable link.",
          messageType: "info",
          timeout: 3000,
        });
        return;
      }
      const link = this.buildAttachmentShareLink(item);
      try {
        await navigator.clipboard.writeText(link);
        this.$store.dispatch("showMessage", {
          message: "Shareable link copied to clipboard",
          messageType: "success",
          timeout: 3000,
        });
      } catch (e) {
        // Clipboard API unavailable (e.g. non-HTTPS context) — surface the link
        // so it can still be copied manually.
        window.prompt("Copy this shareable link:", link);
      }
    },
    async previewOrDownloadAttachment(item, action) {
      // Check if is a email, then open email dialog.
      if (item.showEmail) {
        this.getEmailContent(item);
        return;
      }

      // Check if is excel, download instead.
      if (this.getFileExtension(item.fileUrl) == "xlsx") {
        action = "download";
      }

      this.atchLoadingDialog = !this.atchLoadingDialog;

      let fileUrl = item.fileUrl;
      let azureStorage = item.azureStorage;
      let downloadFileName = item.downloadFileName;
      let fileNameSlug = item.fileNameSlug;

      // Directly open new tab if detected is a web url (etc: https://...)
      const validUrl = this.isValidURL(fileUrl);
      if (validUrl) {
        this.atchLoadingDialog = !this.atchLoadingDialog;

        if (action == "preview") {
          window.open(fileUrl, "_blank");
          return;
        } else {
          window.open(fileUrl, "_blank");
          this.$store.dispatch("showMessage", {
            message:
              "Sorry, due to the privacy issue, this attachment only can be view",
            messageType: "info",
            timeout: 5000,
          });

          return;
        }
      }

      // Azure-stored attachments: "View" opens the permanent, shareable backend
      // link (which redirects to a fresh short-lived SAS) instead of fetching
      // the bytes into a session-only blob: URL. This gives a real, copyable
      // URL in the new tab and never expires when re-clicked.
      if (action == "preview" && azureStorage) {
        this.atchLoadingDialog = false;
        window.open(this.buildAttachmentShareLink(item), "_blank");
        return;
      }

      let attachmentExt = fileUrl.split(".")[1].toLowerCase();
      if (item.fileNameSlug) {
        attachmentExt = fileUrl.split(".").pop();
      }

      fileUrl = fileUrl.replace(/\.[^.]+$/, (match) => match.toLowerCase());

      let responseType = "blob";
      let isImage = false;
      let blobType = `application/${attachmentExt}`;
      let streamUrl = this.apiDownloadOrPreviewAttachment.url;
      let downloadAttachmentName = `${this.companyDetails.companyName}.${attachmentExt}`;
      if (downloadFileName) {
        switch (fileNameSlug) {
          case "CLIENT_REVERT_JD14_FORM":
            downloadAttachmentName = `${this.companyDetails.oriCompanyName}_${downloadFileName}.${attachmentExt}`;
            break;
          case "CLIENT_REVERT_T3_FORM":
            downloadAttachmentName = `${this.companyDetails.oriCompanyName}_${downloadFileName}.${attachmentExt}`;
            break;
          case "flyer":
            downloadAttachmentName = `${downloadFileName}.${attachmentExt}`;
            break;
          case "MANUAL_INVOICE_TO_HRDC":
            downloadAttachmentName = `${downloadFileName}.${attachmentExt}`;
            break;
        }
      }

      if (this.isImage(fileUrl)) {
        responseType = "arraybuffer";
        isImage = true;
        blobType = `image/${attachmentExt.toLowerCase()}`;
      }

      // If is flyer, then directly retrieve from local assets.
      if (!azureStorage) {
        streamUrl = this.apiGetAttachmentBlob.url;
      }

      await this.$axios
        .get(streamUrl, {
          responseType,
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            filePath: fileUrl,
            isImage,
          },
        })
        .then((response) => {
          if (action == "preview") {
            const blobUrl = URL.createObjectURL(
              new Blob([response.data], { type: blobType })
            );

            window.open(blobUrl, "_blank");
          } else if (action == "download") {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", downloadAttachmentName);
            document.body.appendChild(link);
            link.click();
          }

          this.atchLoadingDialog = !this.atchLoadingDialog;
        })
        .catch((error) => {
          if ((error.response.status == 404) | true) {
            this.$store.dispatch("showMessage", {
              message:
                "Failed to Preview/Download attachment, kindly contact administrator",
              messageType: "error",
              timeout: 2000,
            });
          }
          this.atchLoadingDialog = !this.atchLoadingDialog;
        });
    },
    getEmailContent(attachment) {
      try {
        this.loadingDialog = !this.loadingDialog;
        this.emailModalDetails.emailModalHeader = "Email Details";
        this.apiGetEmailDetails.setParams({
          applicationUuid: attachment.applicationUuid,
          stageId: attachment.fileStageId,
        });
        this.apiGetEmailDetails.setCallbackCompleted((response) => {
          if (response.status) {
            const applicationEmail = response.data;
            const applicationEmailDetails =
              applicationEmail.ApplicationEmailDetail;

            const toRecipients = [];
            const ccRecipients = [];
            this.emailModalDetails.emailModalSubject =
              applicationEmail.emailSubject;
            this.emailModalDetails.emailModalContent =
              applicationEmail.emailContent;
            for (const emailDetail of applicationEmailDetails) {
              if (emailDetail.recipientType == "TO") {
                toRecipients.push(emailDetail.recipientEmail);
              }
              if (emailDetail.recipientType == "CC") {
                ccRecipients.push(emailDetail.recipientEmail);
              }
            }

            this.emailModalDetails.emailModalToRecipient = toRecipients;
            this.emailModalDetails.emailModalCcRecipient = ccRecipients;
          }

          this.emailDialog = true;
        });
        this.apiGetEmailDetails.fetch();
      } catch (err) {
        console.log(err);
      } finally {
        this.loadingDialog = !this.loadingDialog;
      }
    },
    renameRemarkStageName(stageName) {
      let stage = stageName;
      switch (stageName) {
        case "Grant Approval No Submitted":
          stage = "Grant Approval No.";
          break;
        case "JD14 T3 Form Submitted":
          stage = "JD14 & T3 Form";
          break;
      }

      return stage;
    },
    checkRoleAllows(checkRole) {
      return this.auth.roles.some((role) => role.slug.includes(checkRole));
    },
    // applicationApproved -> Application Approved
    camelCaseToNormalCase(key) {
      // Split camelCase to words
      const words = key.split(/(?=[A-Z])/);
      // Capitalize the first word and join with spaces
      return words
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    // Application Approved -> APPLICATION_APPROVED
    titleCaseToUppercase(key) {
      if (key !== null) {
        return key.toUpperCase().replace(/ /g, "_");
      }
    },
    checkEmailContent(fileStageId) {
      let fileStagePriority = this.hrdcData.stageIndex[fileStageId];
      let viewEmail = false;
      switch (fileStagePriority) {
        case this.hrdcData.stageSlug.sentConfirmationEmailToClient:
          viewEmail = true;
          break;
        case this.hrdcData.stageSlug.refundEmailSubmission:
          viewEmail = true;
          break;
      }
      return viewEmail;
    },
    copyContent(item) {
      const textarea = document.createElement("textarea");
      textarea.value = item.trim();
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");

      // Remove the temporary textarea
      document.body.removeChild(textarea);

      this.$store.dispatch("showMessage", {
        message: "Copied Successfully!",
        messageType: "info",
        timeout: 2000,
      });
    },
    isValidURL(str) {
      try {
        new URL(str);
        return true;
      } catch (error) {
        return false;
      }
    },
    redirectForm(action) {
      const params = {
        applicationUuid: this.$route.query._ausk,
        stageId: this.$store.state[this.service.key].data.stageId,
        viewId: this.$store.state[this.service.key].data.viewId,
      };

      this.eventStoreCommit(params);

      // Complete application.
      if (action.route == "CompleteApplication") {
        this.toggleCompleteDialog(true);
        return;
      }

      // Delete application.
      if (action.route == "DeleteApplication") {
        this.deleteApplicationDialog = true;
        return;
      }

      this.$store.commit("assignDeveloperData", {
        paginationPageNo: this.$route.query.currentPageNo,
      });

      this.$router.push({
        name: action.route,
        params: {
          backTo: this.eventRoute("ViewApplication"),
        },
        query: {
          viewId: this.$store.state[this.service.key].data.viewId,
          _ausk: this.$route.query._ausk,
        },
      });
    },
    toggleCompleteDialog(val) {
      if (val) {
        this.$refs.completeApplicationConfirmPopup.open({
          title: "Complete Application",
          message: "Are you confirm to complete application?",
          options: {
            btnDisagree: "Cancel",
            btnAgree: "Complete",
          },
          agree: () => {
            this.agreeCallbacCompleteApplication();
          },
          disagree: () => {
            this.disagreeCallbacCompleteApplication();
          },
        });
      }
    },
    agreeCallbacCompleteApplication() {
      this.apiUpdateApplicationStages.setParams({
        applicationUuid: this.$route.query._ausk,
        userUuid: this.auth.uuid,
        updateToStageId: this.hrdcData.stagePriority.hrdcClaimCompleted,
      });
      this.apiUpdateApplicationStages.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;

          if (!status) {
            this.$store.dispatch("showMessage", {
              message: data,
              messageType: "error",
              timeout: 2000,
            });
          }

          if (status) {
            this.$store.dispatch("showMessage", {
              message: data,
              messageType: "success",
              timeout: 2000,
            });
            location.reload();
          }
        } catch (err) {
          console.log(err);
          this.$store.dispatch("showMessage", {
            message: err.message,
            messageType: "error",
            timeout: 2000,
          });
        }
      });
      this.apiUpdateApplicationStages.fetch();
    },
    disagreeCallbacCompleteApplication() {
      //
    },
    deleteApplication() {
      this.deleteApplicationDialog = false;
      this.apiDeleteApplication.setParams({
        applicationUuid: this.$route.query._ausk,
        // companyNameFilledByApplicant: this.companyDetails.oriCompanyName,
      });
      this.apiDeleteApplication.setCallbackCompleted((response) => {
        try {
          if (response) {
            if (response.status) {
              this.$store.commit("assignDeveloperData", {
                paginationPageNo: this.$route.query.currentPageNo,
              });

              this.$router.push({
                name: this.eventRoute("Application"),
                query: {
                  viewId: this.$store.state[this.service.key].data.viewId,
                },
              });
            }
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });
          } else {
            this.$store.dispatch("showMessage", {
              message: "Unable to delete application!",
              messageType: "error",
              timeout: 2000,
            });
          }
        } catch (err) {
          this.$store.dispatch("showMessage", {
            message: "Unable to delete application!",
            messageType: "error",
            timeout: 2000,
          });
        }
        this.deleteApplicationDialog = false;
      });
      this.apiDeleteApplication.fetch();
    },
    goToBack() {
      // read url query
      this.$store.commit("assignDeveloperData", {
        paginationPageNo: this.$route.query.currentPageNo,
      });

      let backToRoute = this.eventRoute("Application");
      if (this.params.backTo) {
        backToRoute = this.params.backTo;
      }

      this.$router.push({
        name: backToRoute,
        params: {
          modelKey: this.model.key,
          backTo: "",
        },
        query: {
          viewId: this.$store.state[this.service.key].data.viewId,
        },
      });
    },
    nextAction() {
      const stageId = this.applicationDetails.stageId;
      const applicationUuid = this.$route.query._ausk;

      let route = this.nextFormRedirect(
        stageId
        // this.applicationDetails.paymentChannel
      );

      let params = {
        applicationUuid,
        stageId,
        viewId: this.$store.state[this.service.key].data.viewId,
      };

      if (route) {
        this.eventStoreCommit(params);

        this.$router.push({
          name: route,
          params: { backTo: this.eventRoute("ViewApplication") },
          query: {
            _ausk: this.$route.query._ausk,
            viewId: "all",
          },
        });
      }
    },
    nextFormRedirect(stageId) {
      let route = null;
      switch (stageId) {
        case this.hrdcData.stagePriority.applicationSubmitted:
          route = this.eventRoute("ViewApplication");
          break;

        case this.hrdcData.stagePriority.applicationApproved:
          route = this.eventRoute("SendEmailToClient");
          break;

        case this.hrdcData.stagePriority.sentConfirmationEmailToClient:
        case this.hrdcData.stagePriority.clientRevertFormIncomplete:
        case this.hrdcData.stagePriority.grantApproved:
        case this.hrdcData.stagePriority.formsCompleted:
          route = this.eventRoute("ClientRevertForm");
          break;

        case this.hrdcData.stagePriority.clientRevertT3Form:
          route = this.eventRoute("ManualInvoiceToHrdc");
          break;

        case this.hrdcData.stagePriority.manualInvoiceToHrdc:
          route = this.eventRoute("ClaimSubmitToHrdc");
          break;

        case this.hrdcData.stagePriority.submittedClaimToHrdc:
          route = this.eventRoute("ClaimApprovedFromHrdc");
          break;

        case this.hrdcData.stagePriority.claimApprovedFromHrdc:
          route = this.eventRoute("PaymentReceivedFromHrdc");
          break;

        case this.hrdcData.stagePriority.creditNoteRequested:
          route = "CreditNoteDocumented";
          break;

        case this.hrdcData.stagePriority.creditNoteDocumented:
        case this.hrdcData.stagePriority.creditNoteGenerated:
          route = this.eventRoute("RefundEmailSubmission");
          break;

        case this.hrdcData.stagePriority.refundEmailSubmission:
          route = this.eventRoute("RefundEmailApproval1");
          break;

        case this.hrdcData.stagePriority.refundEmailApproval1:
          route = this.eventRoute("RefundEmailApproval2");
          break;

        case this.hrdcData.stagePriority.refundEmailApproval2:
        case this.hrdcData.stagePriority.refundEmailApToCimbRejected:
          route = this.eventRoute("RefundEmailArToAp");
          break;

        case this.hrdcData.stagePriority.refundEmailArToAp:
          route = this.eventRoute("RefundEmailApToCimb");
          break;

        case this.hrdcData.stagePriority.apSubmitRefundToCimb:
          route = this.eventRoute("EmailPaymentAdviceToClient");
          break;

        default:
          route = this.eventRoute("ViewApplication");
          break;
      }

      return route;
    },
    getScheduledDate(stageId, startDate = null, endDate = null) {
      let date = null;

      switch (stageId) {
        case this.hrdcData.stagePriority.subscriptionDateScheduled:
        case this.hrdcData.stagePriority.refundEmailUpdated:
          date = `${moment(startDate).format("Y-MM-DD")} - ${moment(
            endDate
          ).format("Y-MM-DD")}`;
          break;
        default:
          date = `${moment(startDate).format("Y-MM-DD")}`;
          break;
      }

      return date;
    },
    changeStageName(stageId, stageName) {
      switch (stageId) {
        case this.hrdcData.stagePriority.invoiceDateScheduled:
          stageName = "Manual Invoice To HRDC";
          break;

        case this.hrdcData.stagePriority.grantApprovalNoSubmitted:
          stageName = "Grant Approval Number Submitted";
          break;

        default:
          break;
      }

      return stageName;
    },
    pendingAction() {
      let pending = null;
      let progressPercentage = 0;
      let keyStage = 1;

      // Check is this is a deleted application.
      if (this.applicationDetails.isApplicationDeleted) {
        return {
          pending: "Application Deleted",
          progressPercentage,
        };
      }

      if (this.hrdcData.stagePriority) {
        switch (this.applicationDetails.stageId) {
          case this.hrdcData.stagePriority.applicationDuplicated:
            pending = "Application Duplicated";
            break;
          case this.hrdcData.stagePriority.applicationSubmitted:
            pending =
              "Pending Approval Application - Application Approval Admin";
            keyStage = 1;
            break;
          case this.hrdcData.stagePriority.applicationApproved:
            pending =
              "Pending Send HRDC Confirmation Email to Client - HRDC Admin";
            keyStage = 2;
            break;
          case this.hrdcData.stagePriority.sentConfirmationEmailToClient:
          case this.hrdcData.stagePriority.grantApproved:
          case this.hrdcData.stagePriority.formsCompleted:
          case this.hrdcData.stagePriority.clientRevertFormIncomplete:
            pending = "Pending Grant Approval & Revert Form - HRDC Admin";
            keyStage = 3;
            break;
          case this.hrdcData.stagePriority.clientRevertT3Form: // Not in use (fallback).
            pending = "Pending Manual Invoice to HRDC - taxPOD Admin";
            keyStage = 3.5;
            break;
          case this.hrdcData.stagePriority.manualInvoiceToHrdc:
            pending = "Pending Submit Claim to HRDC - HRDC Admin";
            keyStage = 4;
            break;
          case this.hrdcData.stagePriority.submittedClaimToHrdc:
            pending = "Pending Claim Approve from HRDC - taxPOD Admin";
            keyStage = 5;
            break;
          case this.hrdcData.stagePriority.claimApprovedFromHrdc:
            pending = "Pending Receive Payment from HRDC - taxPOD Admin";
            keyStage = 6;
            break;
          case this.hrdcData.stagePriority.paymentReceivedFromHrdc:
            pending = "Pending Generate CN - taxPOD Admin";
            if (this.applicationDetails.paymentChannel == "Claim Basis") {
              pending = "Pending Account Enrollment - taxPOD Admin";
            }
            keyStage = 7;
            break;
          case this.hrdcData.stagePriority.creditNoteRequested: // Not in use.
            pending = "Pending CN Documented - taxPOD Admin";
            break;
          case this.hrdcData.stagePriority.creditNoteDocumented:
          case this.hrdcData.stagePriority.creditNoteGenerated:
            pending = "Pending Refund Email Submission - taxPOD Admin";
            keyStage = 8;
            break;
          case this.hrdcData.stagePriority.refundEmailSubmission:
            pending =
              "Pending Refund Email Approval 1 - taxPOD Approval 1 Admin";
            keyStage = 9;
            break;
          case this.hrdcData.stagePriority.refundEmailApproval1:
            pending =
              "Pending Refund Email Approval 2 - taxPOD Approval 2 Admin";
            keyStage = 10;
            break;
          case this.hrdcData.stagePriority.refundEmailApproval2:
          case this.hrdcData.stagePriority.refundEmailApToCimbRejected:
            pending = "Pending Refund Email AR to AP - Finance AR Admin";
            keyStage = 11;
            break;
          case this.hrdcData.stagePriority.refundEmailArToAp:
            pending = "Pending Refund AP to CIMB - Finance AP Admin";
            keyStage = 12;
            break;
          case this.hrdcData.stagePriority.apSubmitRefundToCimb:
            pending = "Pending Email Payment Advice to Client - taxPOD Admin";
            keyStage = 13;
            break;
          case this.hrdcData.stagePriority.emailPaymentAdviceToClient:
          case this.hrdcData.stagePriority.hrdcClaimCompleted:
            pending = "Application Completed";
            keyStage = 14;
            break;
        }
      }

      progressPercentage = this.calculateStagePercentage({
        isPaymentMade: this.applicationDetails.isPaymentMade,
        keyStage,
      });

      return {
        pending,
        progressPercentage,
      };
    },
    castStageName(data) {
      let stageName = null;

      // get jd14, t3, grant status.
      let jd14T3Icon = "mdi mdi-close-circle";
      let grantIcon = "mdi mdi-close-circle";
      let attendanceFormIcon = "mdi mdi-close-circle";
      let jd14T3Color = "red--text";
      let grantColor = "red--text";
      let attendanceFormColor = "red--text";

      let jd14Status = data.applicationAttachment.find(
        (item) =>
          item.stageId == this.hrdcData.stagePriority.clientRevertJd14Form
      );
      let t3Status = data.applicationAttachment.find(
        (item) => item.stageId == this.hrdcData.stagePriority.clientRevertT3Form
      );
      let attendanceForm = data.applicationAttachment.find(
        (item) => item.stageId == this.hrdcData.stagePriority.attendanceForm
      );

      switch (data.stageNameSlug) {
        case "grantApproved":
          if (jd14Status || t3Status) {
            jd14T3Icon = "mdi mdi-alert-circle";
            jd14T3Color = "orange--text";
          }

          grantIcon = "mdi mdi-check-circle";
          grantColor = "green--text";

          if (attendanceForm) {
            attendanceFormIcon = "mdi mdi-check-circle";
            attendanceFormColor = "green--text";
          }

          stageName = this.generateGrantFormIssueStageName({
            jd14T3Icon,
            jd14T3Color,
            grantIcon,
            grantColor,
            attendanceFormIcon,
            attendanceFormColor,
          });

          break;

        case "formsCompleted":
          jd14T3Icon = "mdi mdi-check-circle";
          jd14T3Color = "green--text";

          if (data.grantApprovalNo) {
            grantIcon = "mdi mdi-alert-circle";
            grantColor = "orange--text";
          }

          if (attendanceForm) {
            attendanceFormIcon = "mdi mdi-check-circle";
            attendanceFormColor = "green--text";
          }

          stageName = this.generateGrantFormIssueStageName({
            jd14T3Icon,
            jd14T3Color,
            grantIcon,
            grantColor,
            attendanceFormIcon,
            attendanceFormColor,
          });

          break;

        case "clientRevertT3Form":
          jd14T3Icon = "mdi mdi-check-circle";
          jd14T3Color = "green--text";
          grantIcon = "mdi mdi-check-circle";
          grantColor = "green--text";

          if (attendanceForm) {
            attendanceFormIcon = "mdi mdi-check-circle";
            attendanceFormColor = "green--text";
          }

          stageName = this.generateGrantFormIssueStageName({
            jd14T3Icon,
            jd14T3Color,
            grantIcon,
            grantColor,
            attendanceFormIcon,
            attendanceFormColor,
          });

          break;

        case "clientRevertFormIncomplete":
        case "sentConfirmationEmailToClient":
          if (jd14Status || t3Status) {
            jd14T3Icon = "mdi mdi-alert-circle";
            jd14T3Color = "orange--text";
          }

          if (data.grantApprovalNo) {
            grantIcon = "mdi mdi-alert-circle";
            grantColor = "orange--text";
          }

          if (attendanceForm) {
            attendanceFormIcon = "mdi mdi-check-circle";
            attendanceFormColor = "green--text";
          }

          stageName = this.generateGrantFormIssueStageName({
            jd14T3Icon,
            jd14T3Color,
            grantIcon,
            grantColor,
            attendanceFormIcon,
            attendanceFormColor,
          });

          break;

        default:
          stageName = `<span style="display: inline-flex; align-items: center; padding: 5px 12px; background: #e0f2fe; border: 1px solid #bae6fd; border-radius: 8px; font-size: 0.78rem; font-weight: 700; color: #0369a1; font-family: Lato, sans-serif;">${data.stageName}</span>`;
          break;
      }

      return stageName;
    },
    clearErrorMsg() {
      for (let error in this.errorField) {
        this.errorField[error] = null;
      }
    },
    submitGeneralRemarks() {
      this.loadingDialogBar = !this.loadingDialogBar;

      const fd = new FormData();
      fd.append("applicationUuid", this.$route.query._ausk);
      fd.append("referById", this.auth.uuid);
      fd.append("generalRemarks", this.otherDetails.generalRemarks);

      this.apiSubmitGeneralRemarks.setParams(fd);
      this.apiSubmitGeneralRemarks.setCallbackCompleted((response) => {
        try {
          this.clearErrorMsg();
          this.loadingDialogBar = !this.loadingDialogBar;
          if (!response.status) {
            this.showErrorMessage(response);
            this.$store.dispatch("showMessage", {
              message: response.message,
              messageType: "error",
              timeout: 2000,
            });
          }

          if (response.status) {
            // Change the general remarks.
            const generalRemarkIndex = this.otherDetails.remarks.findIndex(
              (remark) => remark.id === "GENERAL_REMARKS"
            );

            if (this.otherDetails.generalRemarks) {
              if (generalRemarkIndex !== -1) {
                this.otherDetails.remarks[generalRemarkIndex].remarks =
                  this.otherDetails.generalRemarks;
              } else {
                this.otherDetails.remarks.push({
                  id: "GENERAL_REMARKS",
                  stageName: "General Remarks",
                  remarks: this.otherDetails.generalRemarks,
                  remarkBy: this.auth.name,
                });
              }
            } else {
              this.otherDetails.remarks.splice(generalRemarkIndex, 1);
            }

            this.$store.commit("assignDeveloperData", {
              paginationPageNo: this.$route.query.currentPageNo,
            });
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });
            this.generalRemarksDialog = !this.generalRemarksDialog;
          }
        } catch (err) {
          console.log(err);
        }
      });
      this.apiSubmitGeneralRemarks.fetch();
    },
    openUpdateFlagModal() {
      const flagVal = this.applicationDetails.isFlag;
      let flagText = "Unflag";
      if (!flagVal) {
        flagText = "Flag";
      }

      // Add the flag text here.
      this.flagOrUnflagText = flagText;
      this.flagDialog = !this.flagDialog;
    },
    updateFlagRemarkOnly() {
      this.api.setMethod("POST");
      this.api.setUrl(
        `${
          this.$service[this.service.key]
        }/v1/en/console/application/update-flag-remark-only`
      );
      this.api.setParams({
        applicationUuid: this.$route.query._ausk,
        flagRemarks: this.flagRemarks,
        userUuid: this.auth.uuid,
        flagRemarkStageId: this.hrdcData.stagePriority.normalFlag,
      });
      this.api.setCallbackCompleted((response) => {
        const { status, message } = response;

        if (status) {
          // Change the flag remarks.
          const flagRemarkIndex = this.otherDetails.remarks.findIndex(
            (remark) => remark.id === "FLAG_REMARKS"
          );

          if (this.flagRemarks && this.applicationDetails.isFlag) {
            if (flagRemarkIndex !== -1) {
              Object.assign(this.otherDetails.remarks[flagRemarkIndex], {
                remarks: this.flagRemarks,
                remarkBy: this.auth.name,
              });
            } else {
              this.otherDetails.remarks.push({
                id: "FLAG_REMARKS",
                stageName: "Flag Remarks",
                remarks: this.flagRemarks,
                remarkBy: this.auth.name,
              });
            }
          } else {
            // Automatically removed flag remarks.
            this.flagRemarks = null;
            this.otherDetails.remarks.splice(flagRemarkIndex, 1);
          }

          this.$store.dispatch("showMessage", {
            message,
            messageType: "success",
            timeout: 2000,
          });

          this.flagDialog = !this.flagDialog;
          this.clearErrorMsg();
        }

        if (!status) {
          this.showErrorMessage(response);
        }
      });
      this.api.fetch();
    },
    updateFlagAndRemark() {
      this.api.setMethod("POST");
      this.api.setUrl(
        `${
          this.$service[this.service.key]
        }/v1/en/console/application/update-flag-status`
      );
      this.api.setParams({
        userUuid: this.auth.uuid, // Need to record who flag the case.
        applicationUuid: this.$route.query._ausk,
        flagVal: this.applicationDetails.isFlag,
        flagRemarks: this.flagRemarks, // Add the remarks.
      });
      this.api.setCallbackCompleted((response) => {
        try {
          const { status, message } = response;
          if (status) {
            this.applicationDetails.isFlag = !this.applicationDetails.isFlag;

            // Change the flag remarks.
            const flagRemarkIndex = this.otherDetails.remarks.findIndex(
              (remark) => remark.id === "FLAG_REMARKS"
            );

            if (this.flagRemarks) {
              if (flagRemarkIndex !== -1) {
                this.otherDetails.remarks[flagRemarkIndex].remarks =
                  this.flagRemarks;
              } else {
                this.otherDetails.remarks.push({
                  id: "FLAG_REMARKS",
                  stageName: "Flag Remarks",
                  remarks: this.flagRemarks,
                  remarkBy: this.auth.name,
                });
              }
            }

            this.$store.dispatch("showMessage", {
              message,
              messageType: "success",
              timeout: 2000,
            });

            this.flagDialog = !this.flagDialog;
            this.clearErrorMsg();
          }

          if (!status) {
            this.showErrorMessage(response);
          }
        } catch (err) {
          console.log(err);
        }
      });
      this.api.fetch();
    },
    closeUpdateFlagAndRemarkDialog() {
      this.flagDialog = !this.flagDialog;
      this.flagRemarks = null;
      this.clearErrorMsg();
    },
    getFileExtension(fileUrl) {
      const fileExt = fileUrl.split(".").pop();
      return fileExt;
    },
    showFileActionList(e, item) {
      e.preventDefault();
      for (let key in this.showAttachmentAction) {
        this.showAttachmentAction[key] = false;
      }

      this.attachmentPointingItem = null;

      this.attachmentX = e.clientX;
      this.attachmentY = e.clientY;
      this.$nextTick(() => {
        this.showAttachmentAction.menu = true;
        this.attachmentPointingItem = item;
        if (item.showView) {
          this.showAttachmentAction.view = true;
        }
        if (item.showDownload) {
          this.showAttachmentAction.download = true;
        }

        if (this.checkEmailContent(item.fileStageId)) {
          this.showAttachmentAction.email = true;
        }
      });
    },
    generateGrantFormIssueStageName(data) {
      const getStatusStyle = (colorClass) => {
        if (!colorClass) return { bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0' };
        if (colorClass.includes('green')) return { bg: '#dcfce7', color: '#16a34a', border: '#bbf7d0' };
        if (colorClass.includes('orange')) return { bg: '#fef3c7', color: '#d97706', border: '#fde68a' };
        return { bg: '#fee2e2', color: '#dc2626', border: '#fecaca' };
      };

      const jd14Style = getStatusStyle(data.jd14T3Color);
      const grantStyle = getStatusStyle(data.grantColor);
      const attendanceStyle = getStatusStyle(data.attendanceFormColor);

      return `<span style="display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap;">
        <span style="display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; background: ${jd14Style.bg}; border: 1px solid ${jd14Style.border}; border-radius: 8px; font-size: 0.78rem; font-weight: 700; color: ${jd14Style.color}; font-family: Lato, sans-serif;">
          <i aria-hidden="true" class="v-icon notranslate ${data.jd14T3Icon} theme--light" style="font-size: 16px; color: ${jd14Style.color};"></i>
          JD14 & T3 Form
        </span>
        <span style="display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; background: ${grantStyle.bg}; border: 1px solid ${grantStyle.border}; border-radius: 8px; font-size: 0.78rem; font-weight: 700; color: ${grantStyle.color}; font-family: Lato, sans-serif;">
          <i aria-hidden="true" class="v-icon notranslate ${data.grantIcon} theme--light" style="font-size: 16px; color: ${grantStyle.color};"></i>
          Grant No.
        </span>
        <span style="display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; background: ${attendanceStyle.bg}; border: 1px solid ${attendanceStyle.border}; border-radius: 8px; font-size: 0.78rem; font-weight: 700; color: ${attendanceStyle.color}; font-family: Lato, sans-serif;">
          <i aria-hidden="true" class="v-icon notranslate ${data.attendanceFormIcon} theme--light" style="font-size: 16px; color: ${attendanceStyle.color};"></i>
          Attendance Form
        </span>
      </span>`;
    },
    toggleDeleteAttachmentDialog(val, item = null) {
      this.deleteAttachmentItem = item;
      this.deleteAttachmentDialog = val;
    },
    deleteAttachment() {
      const { uuid, type, applicationUuid, fileName } =
        this.deleteAttachmentItem;
      this.apiDeleteAttachment.setParams({
        uuid,
        userUuid: this.auth.uuid,
        applicationUuid,
        deleteAtchStageId: this.hrdcData.stagePriority.deleteAttachment,
        fileName,
      });
      this.apiDeleteAttachment.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;
          let messageType = "error";
          if (status) {
            messageType = "success";

            this.attachmentTreeview = this.attachmentTreeview
              .map((item) => {
                if (item.name === type) {
                  return {
                    ...item,
                    children: item.children.filter(
                      (child) => child.uuid !== uuid
                    ),
                  };
                }
                return item;
              })
              .filter((item) => item.children.length > 0);
          }

          this.$store.dispatch("showMessage", {
            message: data,
            messageType,
            timeout: 2000,
          });
        } catch (err) {
          console.log(err);
          this.$store.dispatch("showMessage", {
            message:
              "Delete attachment failed, kindly contact administrator for assist!",
            messageType: "error",
            timeout: 2000,
          });
        } finally {
          this.toggleDeleteAttachmentDialog(false);
        }
      });
      this.apiDeleteAttachment.fetch();
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .view-page {
  background-color: var(--app-page);
}

.dark-mode .flag-card {
  background: var(--app-card);
}

.dark-mode .flag-header__icon {
  background: var(--app-accent-soft);
}

.dark-mode .flag-header__title {
  color: var(--app-text);
}

.dark-mode .flag-header__subtitle {
  color: var(--app-text-muted);
}

.dark-mode .flag-header__close:hover {
  background: var(--app-elevated);
}

.dark-mode .flag-alert-error {
  background: var(--app-danger-bg);
}

.dark-mode .flag-label {
  color: var(--app-text);
}

.dark-mode .flag-textarea {
  border: 1px solid var(--app-border-strong);
  color: var(--app-text);
  background: var(--app-card);
}

.dark-mode .flag-textarea::placeholder {
  color: var(--app-text-faint);
}

.dark-mode .flag-btn--cancel {
  background: var(--app-card);
  border: 1px solid var(--app-border-strong);
}

.dark-mode .flag-btn--cancel:hover {
  background: var(--app-surface);
}

.dark-mode .flag-btn--outline {
  background: var(--app-card);
}

.dark-mode .flag-btn--outline:hover {
  background: var(--app-accent-soft);
}

.dark-mode .view-header {
  background: var(--app-elevated);
}

.dark-mode .back-btn {
  background: var(--app-card);
  border: 1px solid var(--app-border-strong);
}

.dark-mode .back-btn:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .hero-action-btn {
  background: var(--app-elevated);
  color: var(--app-text-muted);
}

.dark-mode .hero-action-btn:hover {
  background: var(--app-elevated);
  color: var(--app-text);
}

.dark-mode .hero-action-btn--danger {
  background: var(--app-danger-bg);
}

.dark-mode .hero-action-btn--danger:hover {
  background: var(--app-danger-bg);
}

.dark-mode .hero-action-btn--amber {
  background: var(--app-warning-bg);
}

.dark-mode .hero-action-btn--amber:hover {
  background: var(--app-warning-bg);
}

.dark-mode .hero-action-btn--orange {
  background: var(--app-elevated);
  color: var(--app-text-faint);
}

.dark-mode .hero-action-btn--orange:hover {
  background: var(--app-elevated);
  color: var(--app-text-muted);
}

.dark-mode .hero-action-btn--emerald {
  background: var(--app-success-bg);
}

.dark-mode .hero-action-btn--emerald:hover {
  background: var(--app-success-bg);
}

.dark-mode .hero-action-btn--blue {
  background: var(--app-accent-soft);
}

.dark-mode .hero-action-btn--blue:hover {
  background: var(--app-accent-soft);
}

.dark-mode .hero-action-btn--sky {
  background: var(--app-accent-soft);
}

.dark-mode .hero-action-btn--sky:hover {
  background: var(--app-accent-soft);
}

.dark-mode .hero-action-btn--purple {
  background: var(--app-elevated);
  color: var(--app-text-muted);
}

.dark-mode .hero-action-btn--purple:hover {
  background: var(--app-elevated);
  color: var(--app-text);
}

.dark-mode .section-card {
  background: var(--app-elevated);
}

.dark-mode .section-body {
  background: var(--app-card);
}

.dark-mode .section-divider {
  border-top: 1.5px solid var(--app-border-strong);
}

.dark-mode .detail-val {
  border: 1px solid var(--app-border);
}

.dark-mode .empty-state {
  color: var(--app-text-faint);
}

.dark-mode .section-body >>> .v-treeview-node--leaf > .v-treeview-node__root:hover {
  background: var(--app-accent-soft) !important;
}

.dark-mode .section-body >>> .v-treeview-node__toggle {
  color: var(--app-text-faint) !important;
}

.dark-mode .atch-name {
  color: var(--app-text);
}

.dark-mode .atch-row--leaf .atch-name {
  color: var(--app-text);
}

.dark-mode .section-body >>> .v-treeview-node__root > .v-treeview-node__content > .v-treeview-node__label > span {
  color: var(--app-text) !important;
}

.dark-mode .remarks-table-wrap {
  border: 1px solid var(--app-border);
  background: var(--app-elevated);
}

.dark-mode .remarks-table th {
  color: var(--app-text);
  border-bottom: 1px solid var(--app-border-strong);
}

.dark-mode .remarks-table tbody tr {
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .remarks-table tbody tr:hover {
  background-color: var(--app-surface);
}

.dark-mode .remarks-table td {
  color: var(--app-text);
}

.dark-mode .remarks-row--general {
  background: var(--app-danger-bg) !important;
}

.dark-mode .remarks-row--general:hover {
  background: var(--app-danger-bg) !important;
}

.dark-mode .approve-card {
  background: var(--app-card);
}

.dark-mode .approve-card__title {
  color: var(--app-text);
}

.dark-mode .approve-card__message {
  color: var(--app-text-muted);
}

.dark-mode .approve-card__label {
  color: var(--app-neutral-fg);
}

.dark-mode .approve-select__trigger {
  border: 1.5px solid var(--app-border-strong);
  background: var(--app-card);
}

.dark-mode .approve-select__trigger:hover {
  border-color: var(--app-border-strong);
}

.dark-mode .approve-select__placeholder {
  color: var(--app-text-faint);
}

.dark-mode .approve-select__chip-remove:hover {
  background: var(--app-accent-soft);
}

.dark-mode .approve-select__dropdown {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .approve-select__dropdown::-webkit-scrollbar-thumb {
  background: var(--app-elevated);
}

.dark-mode .approve-select__option {
  color: var(--app-text);
}

.dark-mode .approve-select__option:hover {
  background: var(--app-surface);
}

.dark-mode .approve-select__option--selected {
  background: var(--app-accent-soft);
}

.dark-mode .approve-select__option--selected:hover {
  background: var(--app-accent-soft);
}

.dark-mode .approve-select__checkbox {
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .approve-select__empty {
  color: var(--app-text-faint);
}

.dark-mode .approve-card__btn--cancel {
  background: var(--app-card);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .approve-card__btn--cancel:hover {
  background: var(--app-surface);
}

.dark-mode .confirm-card {
  background: var(--app-card);
}

.dark-mode .confirm-card__title {
  color: var(--app-text);
}

.dark-mode .confirm-card__message {
  color: var(--app-text-muted);
}

.dark-mode .confirm-card__btn--cancel {
  background: var(--app-card);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .confirm-card__btn--cancel:hover {
  background: var(--app-surface);
}

.dark-mode .remarks-card {
  background: var(--app-card);
}

.dark-mode .remarks-card__header {
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .remarks-card__title {
  color: var(--app-text);
}

.dark-mode .remarks-card__subtitle {
  color: var(--app-text-faint);
}

.dark-mode .remarks-card__close:hover {
  background: var(--app-elevated);
}

.dark-mode .remarks-card__error-alert {
  background: var(--app-danger-bg);
}

.dark-mode .remarks-card__label {
  color: var(--app-neutral-fg);
}

.dark-mode .remarks-card__textarea {
  border: 1px solid var(--app-border-strong);
  color: var(--app-text);
  background: var(--app-card);
}

.dark-mode .remarks-card__textarea::placeholder {
  color: var(--app-text-faint);
}

.dark-mode .remarks-card__btn--cancel {
  background: var(--app-card);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .remarks-card__btn--cancel:hover {
  background: var(--app-surface);
}

.dark-mode .log-modal__header {
  background: var(--app-card);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .log-modal__title {
  color: var(--app-text);
}

.dark-mode .log-modal__subtitle {
  color: var(--app-text-faint);
}

.dark-mode .log-modal__close:hover {
  background: var(--app-elevated);
}

.dark-mode .log-tabs {
  background: var(--app-card);
}

.dark-mode .log-tab {
  color: var(--app-text-muted);
}

.dark-mode .log-tab:hover {
  color: var(--app-text);
  background: var(--app-surface);
}

.dark-mode .log-tab--active {
  background: var(--app-accent-soft);
}

.dark-mode .log-tab__count {
  background: var(--app-accent-soft);
}

.dark-mode .log-table-wrap {
  border: 1px solid var(--app-border);
  background: var(--app-card);
}

.dark-mode .log-table th {
  color: var(--app-text);
  border-bottom: 1px solid var(--app-border-strong);
}

.dark-mode .log-table tbody tr {
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .log-table tbody tr:hover {
  background: var(--app-surface);
}

.dark-mode .log-table td {
  color: var(--app-text);
}

.dark-mode .log-user {
  color: var(--app-text);
}

.dark-mode .log-date {
  color: var(--app-text-muted);
}

.dark-mode .log-empty {
  color: var(--app-text-faint);
}

.dark-mode .log-modal__footer {
  background: var(--app-card);
  border-top: 1px solid var(--app-border);
}

.dark-mode .log-modal__footer-info {
  color: var(--app-text-faint);
}

.dark-mode .log-modal__footer-btn {
  border: 1.5px solid var(--app-border-strong);
  background: var(--app-card);
}

.dark-mode .log-modal__footer-btn:hover {
  background: var(--app-surface);
}

/* --- Loading skeleton. `.skel-card__header` had NO dark companion, so while
       the page waited on its data every card painted the light theme's
       #cdd1d6 shimmer bar: a near-white slab sitting exactly where the
       section title lands. That is the "flash of light before the header
       turns blue" on a dark-mode refresh — the skeleton, not the header.

       The rest of the skeleton had the opposite failure: frame, line and bar
       were all --app-elevated, so the placeholders were invisible against
       their own card and the shimmer had no second tone to travel to. (The
       `background:` shorthand also reset the light rule's
       `background-size: 200% 100%` back to auto, which froze the animation —
       every dark rule below restates it.)

       Frames now mirror what they stand in for (.view-header / .section-card
       = --app-card + border + inner highlight) so the swap to real content is
       not a jump, and placeholders shimmer one tier ABOVE their surface —
       light steps its placeholders down from the card, dark steps them up. */
.dark-mode .skel-header,
.dark-mode .skel-card {
  background: var(--app-card);
  border: 1px solid var(--app-border);
  box-shadow: inset 0 1px 0 var(--app-inner-highlight);
}

.dark-mode .skel-line,
.dark-mode .skel-bar {
  background: linear-gradient(
    90deg,
    var(--app-form-tile) 25%,
    var(--app-form-tile-hover) 50%,
    var(--app-form-tile) 75%
  );
  background-size: 200% 100%;
}

/* Stands in for `.section-header`, so it keeps that header's bottom rule and
   sits a step above the card — but stays neutral rather than borrowing the
   accent wash, which would read as loaded content. */
.dark-mode .skel-card__header {
  background: linear-gradient(
    90deg,
    var(--app-elevated) 25%,
    var(--app-form-tile) 50%,
    var(--app-elevated) 75%
  );
  background-size: 200% 100%;
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .atch-menu__card {
  background: var(--app-card);
}

.dark-mode .atch-menu__item {
  color: var(--app-text);
}

.dark-mode .atch-menu__item--danger:hover {
  background: var(--app-danger-bg);
}

.dark-mode .atch-menu__divider {
  background: var(--app-elevated);
}

.dark-mode .email-modal {
  background: var(--app-card);
}

.dark-mode .email-modal__header {
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .email-modal__icon {
  background: var(--app-accent-soft);
}

.dark-mode .email-modal__title {
  color: var(--app-text);
}

.dark-mode .email-modal__subject {
  color: var(--app-text-muted);
}

.dark-mode .email-modal__close {
  border: 1px solid var(--app-border);
  background: var(--app-card);
}

.dark-mode .email-modal__close:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .email-modal__recipients {
  background: var(--app-surface);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .email-modal__recipient-label {
  color: var(--app-text-faint);
}

.dark-mode .email-modal__chip {
  background: var(--app-accent-soft);
}

.dark-mode .email-modal__chip--cc {
  background: var(--app-purple-bg);
}

.dark-mode .email-modal__body {
  color: var(--app-text);
}

.dark-mode .email-modal__body::-webkit-scrollbar-thumb {
  background: var(--app-elevated);
}

.dark-mode .email-modal__footer {
  border-top: 1px solid var(--app-border);
}

.dark-mode .email-modal__btn {
  background: var(--app-card);
  color: var(--app-text-muted);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .email-modal__btn:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .participant-slider__badge {
  background: var(--app-accent-soft);
}

.dark-mode .participant-slider__btn {
  border: 1px solid var(--app-border);
  background: var(--app-card);
}

.dark-mode .participant-slider__btn:hover:not(:disabled) {
  background: var(--app-accent-soft);
}

.dark-mode .participant-slider__btn .v-icon {
  color: var(--app-text-muted);
}

/* >>> end auto-dark <<< */
/* --- Section headers — the neutral dark band, same treatment as
       hrdc/views/application/View.vue.

       The light theme paints these with a saturated sky gradient
       (#0ea5e9 -> #38bdf8); dark used to carry that over as a flat, undiluted
       --app-accent bar, and a column of those on charcoal is the glare this
       page was reported for. The band is now neutral — var(--app-elevated),
       the card frame's own tier, one step above the --app-card body it caps, closed by --app-border and lifted by the same
       `inset 0 1px 0` highlight every other raised surface uses — so it reads
       as a header by GEOMETRY rather than by hue. The accent survives only at
       sizes small enough to be a cue instead of a field: a 2px spine that fades
       out down the band, and the icon glyph. */
.dark-mode .section-header {
  position: relative;
  background-color: var(--app-elevated);
  background-image: none;
  border-bottom: 1px solid var(--app-border);
  box-shadow: inset 0 1px 0 var(--app-inner-highlight);
}

/* What is left of the accent: 2px wide and faded top-to-bottom, so it marks
   the header's start without drawing a hard full-height rail.
   .section-card's overflow clips it to the card radius. */
.dark-mode .section-header::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(
    180deg,
    rgba(var(--app-accent-rgb), 0.55),
    rgba(var(--app-accent-rgb), 0.12)
  );
}

.dark-mode .section-header__title {
  color: var(--app-text);
  font-weight: 700;
  letter-spacing: 1.6px;
}

/* The icon can hold the accent now that the band behind it is neutral.
   `color="white"` on the tag compiles to .white--text (0,1,0 + !important);
   this is (0,3,0 + !important) and wins. */
.dark-mode .section-header .v-icon {
  color: var(--app-accent) !important;
  opacity: 0.85;
}

/* ============================================================================
   DARK MODE — APPLICATION LOG DIALOG (hand-authored addendum)
   ----------------------------------------------------------------------------
   The auto-dark block above covers the log dialog's header, tabs, table body
   and footer, but it skipped every rule whose light value is not a plain white
   surface — which left the dialog reading as light:

     · .log-modal itself (#f5f6f8) — the sheet the header/tabs/footer sit on;
     · .log-table thead tr (#e5e7ec) — a light grey band above dark rows;
     · .log-status-pill / .log-modal__icon / .log-rail-dot — pale blue washes;
     · the accent pieces whose *foreground* stayed a light-theme hex
       (#0369a1 on the tab count, #0ea5e9 on the active tab and footer button),
       which is too dark to read once the fill behind it goes translucent.

   The timeline rail is new markup and has no auto-dark companion at all, so
   every one of its pieces is tokenised here.

   Colours are token references only (--app-*, src/plugins/theme-dark.css).
   ============================================================================ */

.dark-mode .log-modal {
  background: var(--app-surface);
}

.dark-mode .log-modal__icon {
  background: var(--app-accent-soft);
}

.dark-mode .log-tab--active {
  color: var(--app-accent);
  border-bottom-color: var(--app-accent);
}

.dark-mode .log-tab__count {
  color: var(--app-accent-strong);
}

.dark-mode .log-table thead tr {
  background: var(--app-elevated);
}

/* A hover that goes darker than the row reads as a hole in dark mode — use the
   palette's white wash instead. */
.dark-mode .log-table tbody tr:hover {
  background: var(--app-hover);
}

.dark-mode .log-status-pill {
  background: var(--app-info-bg);
  color: var(--app-info-fg);
}

/* Timeline rail */
.dark-mode .log-rail-cap {
  color: var(--app-text-faint);
}

.dark-mode .log-rail-cap--start {
  color: var(--app-accent);
}

.dark-mode .log-rail-dot {
  background: var(--app-accent);
  box-shadow: 0 0 0 4px var(--app-accent-soft);
}

.dark-mode .log-rail-track {
  background: linear-gradient(var(--app-accent), var(--app-elevated));
}

.dark-mode .log-rail-arrow {
  border-top: 10px solid var(--app-border-strong);
}

.dark-mode .log-modal__footer-btn {
  color: var(--app-accent);
}

.dark-mode .log-modal__footer-btn:hover {
  border-color: var(--app-accent);
}

/* Mobile card-ify borders — the light block's #e5e7eb / #f1f5f9 hairlines and
   #94a3b8 labels are invisible-to-glaring against a dark card. */
@media (max-width: 599px) {
  .dark-mode .log-table tbody tr {
    border: 1px solid var(--app-border);
  }

  .dark-mode .log-table td {
    border-bottom: 1px solid var(--app-border);
  }

  .dark-mode .log-table td::before {
    color: var(--app-text-faint);
  }
}

/* ============================================
   PAGE WRAPPER
   ============================================ */
.view-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding-top: 24px !important;
  padding-bottom: 60px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  zoom: 1.1;
}

/* ============================================
   FLAG REMARK DIALOG
   ============================================ */
.flag-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

.flag-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 0;
}

.flag-header__left {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.flag-header__icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #e0f2fe;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.flag-header__title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
}

.flag-header__subtitle {
  font-size: 13px;
  font-weight: 400;
  color: #64748b;
  margin-top: 2px;
}

.flag-header__close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.flag-header__close:hover {
  background: #f1f5f9;
}

.flag-body {
  padding: 20px 24px;
}

.flag-alert-error {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  margin-bottom: 16px;
}

.flag-alert-error__icon {
  margin-top: 1px;
  flex-shrink: 0;
}

.flag-alert-error__text {
  font-size: 13px;
  font-weight: 500;
  color: #dc2626;
  line-height: 1.5;
  white-space: pre-wrap;
}

.flag-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.flag-textarea {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  color: #1e293b;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  background: #fff;
  box-sizing: border-box;
}

.flag-textarea::placeholder {
  color: #9ca3af;
}

.flag-textarea:focus {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}

.flag-textarea--error {
  border-color: #f87171;
}

.flag-textarea--error:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15);
}

.flag-field-error {
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
}

.flag-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 24px 24px;
}

.flag-btn {
  padding: 9px 22px;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.flag-btn--cancel {
  background: #fff;
  border: 1px solid #d1d5db;
  color: #0ea5e9;
}

.flag-btn--cancel:hover {
  background: #f8fafc;
  border-color: #0ea5e9;
}

.flag-btn--primary {
  background: #56bde8;
  border: 1px solid #56bde8;
  color: #fff;
}

.flag-btn--primary:hover {
  background: #3baee0;
  border-color: #3baee0;
}

.flag-btn--outline {
  background: #fff;
  border: 1px solid #56bde8;
  color: #56bde8;
}

.flag-btn--outline:hover {
  background: #f0f9ff;
}

/* Flagged case banner */
.flag-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #ef4444, #f87171);
  border-radius: 14px;
  margin-bottom: 16px;
}

.flag-banner__icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.flag-banner__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.flag-banner__title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.3px;
}

.flag-banner__remarks {
  font-size: 0.78rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

/* ============================================
   VIEW HEADER
   ============================================ */
.view-header {
  background: #eaecef;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 24px;
  overflow: hidden;
}

.view-header__top {
  display: flex;
  align-items: center;
  padding: 24px 28px 16px;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  margin-right: 16px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.view-header__info {
  flex: 1;
  min-width: 0;
}

.view-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

.view-header__status {
  margin-top: 10px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.2px;
}

.view-header__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 16px;
}

/* Hero action buttons */
.hero-action-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.hero-action-btn:hover {
  background: #e2e8f0;
  color: #334155;
  transform: translateY(-1px);
}

/* Color variants */
.hero-action-btn--danger {
  background: #fef2f2;
  color: #ef4444;
}

.hero-action-btn--danger:hover {
  background: #fee2e2;
  color: #dc2626;
}

.hero-action-btn--amber {
  background: #fffbeb;
  color: #f59e0b;
}

.hero-action-btn--amber:hover {
  background: #fef3c7;
  color: #d97706;
}

.hero-action-btn--orange {
  background: #f1f5f9;
  color: #94a3b8;
}

.hero-action-btn--orange:hover {
  background: #e2e8f0;
  color: #64748b;
}

.hero-action-btn--emerald {
  background: #ecfdf5;
  color: #10b981;
}

.hero-action-btn--emerald:hover {
  background: #d1fae5;
  color: #059669;
}

.hero-action-btn--blue {
  background: #eff6ff;
  color: #3b82f6;
}

.hero-action-btn--blue:hover {
  background: #dbeafe;
  color: #2563eb;
}

.hero-action-btn--teal {
  background: #f0fdfa;
  color: #14b8a6;
}

.hero-action-btn--teal:hover {
  background: #ccfbf1;
  color: #0d9488;
}

.hero-action-btn--sky {
  background: #f0f9ff;
  color: #0ea5e9;
}

.hero-action-btn--sky:hover {
  background: #e0f2fe;
  color: #0284c7;
}

.hero-action-btn--purple {
  background: #f1f5f9;
  color: #64748b;
}

.hero-action-btn--purple:hover {
  background: #e2e8f0;
  color: #334155;
}

/* Progress Bar */
.progress-wrap {
  padding: 0 28px 20px;
}

.progress-track {
  height: 4px;
  background: #eceef1;
  border-radius: 100px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #7eb8da, #5a9bbf);
  border-radius: 100px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

/* ============================================
   CARDS GRID LAYOUT
   ============================================ */
.cards-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  max-width: 100%;
  overflow: hidden;
}

@media (min-width: 960px) {
  .cards-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.cards-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
}

/* ============================================
   SECTION CARDS
   ============================================ */
.section-card {
  background: #eaecef;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.section-card:hover {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
}

.section-header {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  padding: 13px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header__title {
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.section-body {
  padding: 22px;
  background: #fff;
}

.section-divider {
  border: none;
  border-top: 1.5px solid #cbd5e1;
  margin: 12px 0;
}

/* ============================================
   DETAIL ROWS
   ============================================ */
.detail-row {
  display: flex;
  align-items: center;
  padding: 6px 0;
  cursor: pointer;
}

.detail-label {
  flex: 0 0 180px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3239;
  line-height: 1.6;
  padding-top: 1px;
}

.detail-val {
  flex: 1;
  font-size: 0.85rem;
  color: #1a1e24;
  line-height: 1.5;
  word-break: break-word;
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  background: #f1f3f5;
  border: 1px solid #e2e4e8;
  border-radius: 8px;
  padding: 7px 14px;
  transition: all 0.15s ease;
  cursor: pointer;
}

.detail-val:hover {
  background: #eef1f5;
  border-color: #c9cdd3;
  color: #2c3038;
}

.detail-val--lowercase {
  text-transform: none;
}

.detail-group {
  /* container for participant carousel items */
}

/* Copy icon */
.copy-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
  margin-left: 8px;
  color: #a0aab4 !important;
}

.detail-row:hover .copy-icon {
  opacity: 1;
}

/* ============================================
   BADGES
   ============================================ */
.badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: 10px;
  font-size: 0.78rem;
  font-weight: 600;
}

.stage-badges {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.badge--green {
  background: #edf7f0;
  color: #3a7d53;
  font-size: 0.88rem;
  font-weight: 700;
}

.badge--amber {
  background: #fdf6e3;
  color: #8a6d2b;
  font-size: 0.88rem;
  font-weight: 700;
}

/* ============================================
   EMPTY STATE
   ============================================ */
.empty-state {
  color: #94a3b8;
  text-align: center;
  padding: 28px 0;
  font-size: 0.88rem;
  font-weight: 500;
}

/* ============================================
   ATTACHMENT ITEMS
   ============================================ */
.section-body >>> .v-treeview-node__root {
  min-height: 38px !important;
  padding: 0 8px !important;
}

.section-body >>> .v-treeview-node--leaf > .v-treeview-node__root {
  border: 1px solid transparent;
  border-radius: 8px;
  margin: 2px 0;
  transition: all 0.15s ease;
}

.section-body >>> .v-treeview-node--leaf > .v-treeview-node__root:hover {
  background: #f0f9ff !important;
  border-color: #bae6fd;
  border-radius: 8px;
}

.section-body >>> .v-treeview-node__toggle {
  color: #94a3b8 !important;
}

.atch-row {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 8px 4px 0;
  border-radius: 8px;
  transition: all 0.15s ease;
}

.atch-row--leaf {
  min-width: 300px;
}

.atch-row--leaf:hover {
  background: transparent;
}

.atch-icon {
  margin-right: 10px;
  flex-shrink: 0;
  font-size: 20px !important;
}

.atch-name {
  font-family: 'Lato', sans-serif;
  font-size: 0.82rem;
  color: #334155;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.atch-row--leaf .atch-name {
  color: #1e293b;
}

.atch-label-fill {
  width: 100%;
  height: 32px;
  cursor: pointer;
}

.section-body >>> .v-treeview-node__root > .v-treeview-node__content > .v-treeview-node__label > span {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.85rem !important;
  font-weight: 700 !important;
  color: #1e293b !important;
  letter-spacing: 0.3px;
}

/* ============================================
   REMARKS TABLE
   ============================================ */
.remarks-title {
  font-size: 0.72rem;
  font-weight: 600;
  color: #7a8290;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 14px;
}

.remarks-table-wrap {
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  background: #eaecef;
}

.remarks-table {
  width: 100%;
  font-size: 0.85rem;
  border-collapse: collapse;
}

.remarks-table thead tr {
  background: #e5e7ec;
}

.remarks-table th {
  text-align: left;
  padding: 14px 20px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #374151;
  border-bottom: 1px solid #d1d5db;
}

.remarks-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
}

.remarks-table tbody tr:last-child {
  border-bottom: none;
}

.remarks-table tbody tr:hover {
  background-color: #fafafa;
}

.remarks-table td {
  padding: 14px 20px;
  font-size: 0.82rem;
  color: #1f2937;
  font-weight: 500;
}

.remarks-row--general {
  background: #fef2f2 !important;
}

.remarks-row--general:hover {
  background: #fee2e2 !important;
}

/* ============================================
   MOBILE RESPONSIVE
   ============================================ */
@media (max-width: 599px) {
  .view-page {
    padding: 12px 8px !important;
    zoom: 1;
  }

  /* Log dialog — stack each table row into a labelled card on mobile */
  .log-modal__body {
    padding: 14px;
    max-height: 64vh;
  }

  .log-modal__header {
    padding: 18px 20px 14px;
  }

  .log-modal__title {
    font-size: 0.95rem;
  }

  .log-tabs {
    padding: 0 12px;
    gap: 0;
  }

  .log-tab {
    flex: 1;
    justify-content: center;
    font-size: 0.78rem;
    padding: 10px 12px;
  }

  /* Card-ify the table: hide the header row, render rows as blocks */
  .log-table-wrap {
    border: none;
    background: transparent;
    overflow: visible;
  }

  .log-table {
    min-width: 0;
    font-size: 0.8rem;
  }

  .log-table thead {
    display: none;
  }

  /* The left timeline rail is desktop-only; cards don't need it */
  .log-rail {
    display: none;
  }

  .log-table,
  .log-table tbody,
  .log-table tr,
  .log-table td {
    display: block;
    width: 100%;
  }

  .log-table tbody tr {
    background: transparent;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    margin-bottom: 12px;
    overflow: hidden;
  }

  .log-table tbody tr:last-child {
    margin-bottom: 0;
  }

  .log-table tbody tr:hover {
    background: transparent;
  }

  .log-table td {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 5px 14px;
    border: none;
    border-bottom: 1px solid #f1f5f9;
    white-space: normal;
    text-align: right;
  }

  .log-table tbody tr td:last-child {
    border-bottom: none;
  }

  .log-table td::before {
    content: attr(data-label);
    flex-shrink: 0;
    font-size: 0.66rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    text-align: left;
  }

  .log-date {
    word-break: break-word;
  }

  .log-modal__footer {
    padding: 14px 20px;
  }

  .view-header {
    border-radius: 14px;
    margin-bottom: 16px;
  }

  .view-header__top {
    flex-wrap: wrap;
    padding: 16px 16px 12px;
  }

  .back-btn {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    margin-right: 12px;
  }

  .view-header__title {
    font-size: 1.05rem;
    white-space: normal;
  }

  .status-badge {
    font-size: 0.65rem;
    padding: 4px 10px;
  }

  .view-header__actions {
    width: 100%;
    margin-left: 0;
    margin-top: 10px;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 4px;
  }

  .hero-action-btn {
    width: 36px;
    height: 36px;
  }

  .progress-wrap {
    padding: 0 16px 14px;
    width: 100%;
    box-sizing: border-box;
  }

  .cards-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .cards-column {
    gap: 16px;
  }

  .section-card {
    border-radius: 14px;
  }

  .section-header {
    padding: 12px 16px;
  }

  .section-header__title {
    font-size: 0.65rem;
    letter-spacing: 1.5px;
  }

  .section-body {
    padding: 14px;
  }

  .detail-row {
    flex-direction: column;
    align-items: stretch;
    padding: 8px 0;
  }

  .detail-label {
    flex: none;
    font-size: 0.72rem;
    margin-bottom: 4px;
  }

  .detail-val {
    font-size: 0.82rem;
    padding: 8px 12px;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .badge {
    font-size: 0.72rem;
    padding: 4px 10px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .remarks-table-wrap {
    border-radius: 10px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .remarks-table {
    min-width: 480px;
  }

  .remarks-table th,
  .remarks-table td {
    padding: 10px 12px;
    font-size: 0.72rem;
  }

  .atch-name {
    font-size: 0.75rem;
    word-break: break-all;
    white-space: normal;
  }

  .atch-row {
    max-width: 100%;
    overflow: hidden;
  }

  .atch-row--leaf {
    min-width: auto;
  }

  .section-body >>> .v-treeview-node__root {
    padding-left: 4px !important;
  }

  .section-body >>> .v-treeview-node__content {
    overflow: hidden;
  }

  .remarks-fab {
    bottom: 16px;
    right: 16px;
    width: 46px;
    height: 46px;
    border-radius: 12px;
  }

  .section-divider {
    margin: 8px 0;
  }

  .view-page {
    overflow-x: hidden;
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  .section-card {
    border-radius: 12px;
    overflow: hidden;
    max-width: 100%;
    box-sizing: border-box;
  }

  .section-body {
    padding: 14px 12px;
    overflow: hidden;
    word-break: break-word;
  }

  .view-page.container--fluid {
    padding: 12px 4px !important;
  }

  .cards-column {
    max-width: 100%;
    overflow: hidden;
  }

  .empty-state {
    font-size: 0.82rem;
    padding: 16px 8px;
  }
}

/* Tablet breakpoint */
@media (min-width: 600px) and (max-width: 959px) {
  .view-page {
    zoom: 1;
  }

  .view-header__top {
    padding: 20px 20px 14px;
  }

  .view-header__title {
    font-size: 1.15rem;
  }

  .view-header__actions {
    flex-wrap: wrap;
    gap: 4px;
  }

  .cards-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .section-body {
    padding: 18px;
  }

  .detail-label {
    flex: 0 0 160px;
  }

  .progress-wrap {
    width: 100%;
    box-sizing: border-box;
  }

  .remarks-table-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .remarks-table {
    min-width: 480px;
  }

  .detail-row {
    gap: 8px;
  }

  .remarks-fab {
    bottom: 20px;
    right: 20px;
  }

  .view-page {
    overflow-x: hidden;
  }
}

/* ============================================
   ACTION DROPDOWN (3-dot menu)
   ============================================ */
.action-dropdown {
  border-radius: 0 !important;
  overflow: hidden;
}

.action-dropdown__item {
  min-height: 40px !important;
  padding: 0 20px !important;
  transition: background-color 0.15s ease;
}

/* Tokens, not literals. These three rules carry `!important`, so a hardcoded
   light colour here outranks every global dark rule no matter what specificity
   the theme sheet uses — the menu kept its white hover wash and near-black text
   on the dark panel, leaving the items invisible until hovered. The tokens
   resolve to the same light values they replaced (--app-accent IS #0ea5e9 in
   light), so light mode is unchanged and dark mode follows the theme. */
.action-dropdown__item:hover {
  background-color: var(--app-hover) !important;
}

.action-dropdown__text {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.85rem !important;
  font-weight: 500 !important;
  color: var(--app-text) !important;
}

.action-dropdown__item:hover .action-dropdown__text {
  color: var(--app-accent) !important;
}

/* ============================================
   APPROVE APPLICATION DIALOG
   ============================================ */
.approve-card {
  background: #fff;
  border-radius: 20px;
  padding: 32px 32px 24px;
  text-align: center;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

.approve-card__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 10px;
}

.approve-card__message {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 20px;
  line-height: 1.5;
}

.approve-card__field {
  text-align: left;
  margin-bottom: 20px;
}

.approve-card__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
  display: block;
}

/* Custom Tailwind-style multi-select dropdown for the Approve dialog. */
.approve-select { position: relative; }
.approve-select__trigger {
  display: flex; align-items: center; justify-content: space-between;
  min-height: 42px;
  padding: 6px 12px;
  border: 1.5px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
}
.approve-select__trigger:hover { border-color: #94a3b8; }
.approve-select__trigger--open {
  border-color: #56bde8;
  box-shadow: 0 0 0 3px rgba(86, 189, 232, 0.12);
}
.approve-select__trigger--error { border-color: #ef4444; }
.approve-select__value {
  display: flex; flex-wrap: wrap; gap: 6px; flex: 1; align-items: center;
}
.approve-select__placeholder { color: #94a3b8; font-size: 0.85rem; }
.approve-select__chip {
  display: inline-flex; align-items: center; gap: 4px;
  background: #f0f7ff;
  color: #0369a1;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  padding: 3px 6px 3px 10px;
  font-size: 0.8rem; font-weight: 500; line-height: 1.4;
}
.approve-select__chip-remove {
  display: flex; align-items: center; justify-content: center;
  width: 18px; height: 18px;
  border: none; background: none; border-radius: 4px;
  cursor: pointer; padding: 0;
  transition: background 0.15s;
}
.approve-select__chip-remove:hover { background: #e0f2fe; }
.approve-select__arrow {
  transition: transform 0.2s ease;
  flex-shrink: 0; margin-left: 4px;
}
.approve-select__arrow--open { transform: rotate(180deg); }
.approve-select__dropdown {
  margin-top: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  max-height: 180px;
  overflow-y: auto;
  padding: 4px;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}
.approve-select__dropdown::-webkit-scrollbar { width: 5px; }
.approve-select__dropdown::-webkit-scrollbar-track { background: transparent; }
.approve-select__dropdown::-webkit-scrollbar-thumb {
  background: #cbd5e1; border-radius: 4px;
}
.approve-select__dropdown::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
.approve-select__option {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 12px;
  border-radius: 7px;
  font-size: 0.85rem;
  color: #334155;
  cursor: pointer;
  transition: background 0.12s;
}
.approve-select__option:hover { background: #f8fafc; }
.approve-select__option--selected { background: #f0f9ff; }
.approve-select__option--selected:hover { background: #e0f2fe; }
.approve-select__checkbox {
  width: 18px; height: 18px;
  border: 1.5px solid #cbd5e1;
  border-radius: 5px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;
}
.approve-select__checkbox--checked {
  background: #56bde8; border-color: #56bde8;
}
.approve-select__empty {
  padding: 16px; text-align: center;
  color: #94a3b8; font-size: 0.85rem;
}
.approve-select__error {
  margin-top: 6px; font-size: 0.8rem; color: #ef4444;
}
.approve-dropdown-enter-active,
.approve-dropdown-leave-active { transition: all 0.15s ease; }
.approve-dropdown-enter,
.approve-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.approve-card__actions {
  display: flex;
  gap: 12px;
}

.approve-card__btn {
  flex: 1;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.approve-card__btn--cancel {
  background: #fff;
  color: #0ea5e9;
  border: 1.5px solid #d1d5db;
}

.approve-card__btn--cancel:hover {
  background: #f8fafc;
  border-color: #0ea5e9;
}

.approve-card__btn--confirm {
  background: #56bde8;
  color: #fff;
  border: none;
}

.approve-card__btn--confirm:hover {
  background: #3dadd8;
  box-shadow: 0 4px 12px rgba(86, 189, 232, 0.3);
}

/* ============================================
   CONFIRM DIALOGS (Delete)
   ============================================ */
.confirm-card {
  background: #fff;
  border-radius: 20px;
  padding: 32px 32px 24px;
  text-align: center;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

.confirm-card__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 10px;
}

.confirm-card__message {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 8px;
  line-height: 1.5;
}

.confirm-card__hint {
  font-size: 0.8rem;
  color: #ef4444;
  margin: 0 0 20px;
}

.confirm-card__actions {
  display: flex;
  gap: 12px;
}

.confirm-card__btn {
  flex: 1;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.confirm-card__btn--cancel {
  background: #fff;
  color: #0ea5e9;
  border: 1.5px solid #d1d5db;
}

.confirm-card__btn--cancel:hover {
  background: #f8fafc;
  border-color: #0ea5e9;
}

.confirm-card__btn--danger {
  background: #ef4444;
  color: #fff;
  border: none;
}

.confirm-card__btn--danger:hover {
  background: #dc2626;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

/* ============================================
   GENERAL REMARKS DIALOG
   ============================================ */
.remarks-card {
  background: #fff;
  border-radius: 16px;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow: hidden;
}

.remarks-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px 18px;
  border-bottom: 1px solid #e8eaed;
}

.remarks-card__header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.remarks-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #f0f7ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.remarks-card__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 2px;
}

.remarks-card__subtitle {
  font-size: 0.78rem;
  color: #94a3b8;
  margin: 0;
}

.remarks-card__close {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.remarks-card__close:hover {
  background: #f1f5f9;
}

.remarks-card__body {
  padding: 24px 28px;
}

.remarks-card__error-alert {
  display: flex;
  align-items: center;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 0.85rem;
  color: #dc2626;
}

.remarks-card__label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
}

.remarks-card__textarea {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: 'Lato', sans-serif;
  color: #1e293b;
  background: #fff;
  resize: vertical;
  transition: border-color 0.15s, box-shadow 0.15s;
  outline: none;
  line-height: 1.6;
}

.remarks-card__textarea:focus {
  border-color: #56bde8;
  box-shadow: 0 0 0 3px rgba(86, 189, 232, 0.12);
}

.remarks-card__textarea::placeholder {
  color: #94a3b8;
}

.remarks-card__footer {
  display: flex;
  gap: 12px;
  padding: 0 28px 24px;
  justify-content: flex-end;
}

.remarks-card__btn {
  padding: 10px 24px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}

.remarks-card__btn--cancel {
  background: #fff;
  color: #0ea5e9;
  border: 1.5px solid #d1d5db;
}

.remarks-card__btn--cancel:hover {
  background: #f8fafc;
  border-color: #0ea5e9;
}

.remarks-card__btn--confirm {
  background: #56bde8;
  color: #fff;
  border: none;
}

.remarks-card__btn--confirm:hover {
  background: #3dadd8;
  box-shadow: 0 4px 12px rgba(86, 189, 232, 0.3);
}

/* ============================================
   APPLICATION LOG DIALOG
   ============================================ */
.log-modal {
  background: #f5f6f8;
  border-radius: 16px;
  font-family: 'Lato', sans-serif;
  overflow: hidden;
}

.log-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px 18px;
  background: #fff;
  border-bottom: 1px solid #e8eaed;
}

.log-modal__header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.log-modal__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #f0f7ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.log-modal__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 2px;
}

.log-modal__subtitle {
  font-size: 0.78rem;
  color: #94a3b8;
  margin: 0;
}

.log-modal__close {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.log-modal__close:hover {
  background: #f1f5f9;
}

.log-tabs {
  display: flex;
  gap: 4px;
  padding: 14px 28px 0;
  background: #fff;
}

.log-tab {
  display: inline-flex;
  align-items: center;
  padding: 10px 18px;
  border: none;
  background: none;
  cursor: pointer;
  font-family: 'Lato', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  color: #64748b;
  border-bottom: 2px solid transparent;
  transition: all 0.15s;
  border-radius: 8px 8px 0 0;
}

.log-tab:hover {
  color: #334155;
  background: #f8fafc;
}

.log-tab--active {
  color: #0ea5e9;
  border-bottom-color: #0ea5e9;
  background: #f0f9ff;
}

.log-tab__count {
  margin-left: 8px;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}

.log-modal__body {
  padding: 20px 28px;
  max-height: 450px;
  overflow-y: auto;
}

.log-table-wrap {
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  background: #fff;
}

.log-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.log-table thead tr {
  background: #e5e7ec;
}

.log-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #d1d5db;
}

.log-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.1s;
}

.log-table tbody tr:last-child {
  border-bottom: none;
}

.log-table tbody tr:hover {
  background: #fafafa;
}

.log-table td {
  padding: 12px 16px;
  color: #1f2937;
  font-weight: 500;
}

/* External left timeline rail (start at top → arrow at bottom) */
.log-table-layout {
  display: flex;
  align-items: stretch;
  gap: 14px;
}

.log-table-layout .log-table-wrap {
  flex: 1;
  min-width: 0;
}

.log-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding: 4px 0;
}

.log-rail-cap {
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
  white-space: nowrap;
}

.log-rail-cap--start {
  color: #0ea5e9;
  margin-bottom: 8px;
}

.log-rail-cap--end {
  margin-top: 8px;
}

.log-rail-dot {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: #0ea5e9;
  box-shadow: 0 0 0 4px #e0f2fe;
  flex-shrink: 0;
}

.log-rail-track {
  flex: 1;
  width: 2px;
  min-height: 24px;
  background: linear-gradient(#0ea5e9, #cbd5e1);
  margin: 3px 0;
}

.log-rail-arrow {
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 10px solid #cbd5e1;
  flex-shrink: 0;
}

.log-user {
  font-weight: 600;
  color: #334155;
}

.log-status-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  background: #f0f7ff;
  color: #0369a1;
  font-size: 0.75rem;
  font-weight: 600;
}

.log-date {
  color: #64748b;
  font-size: 0.78rem;
}

.log-empty {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  font-size: 0.88rem;
}

.log-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 28px;
  background: #fff;
  border-top: 1px solid #e8eaed;
}

.log-modal__footer-info {
  font-size: 0.78rem;
  color: #94a3b8;
  font-weight: 500;
}

.log-modal__footer-btn {
  padding: 10px 28px;
  border: 1.5px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  color: #0ea5e9;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.15s;
}

.log-modal__footer-btn:hover {
  background: #f8fafc;
  border-color: #0ea5e9;
}

/* ============================================
   PAGE LOADER / SKELETON
   ============================================ */
.page-loader {
  padding: 0;
}

.skel-header {
  background: #eaecef;
  border-radius: 14px;
  padding: 28px;
  margin-bottom: 24px;
}

.skel-line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #dfe1e5 25%, #e8eaed 50%, #dfe1e5 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  margin-bottom: 10px;
}

.skel-line--sm { width: 30%; }
.skel-line--md { width: 50%; }
.skel-line--lg { width: 70%; height: 14px; }
.skel-line--full { width: 100%; }

.skel-bar {
  height: 6px;
  border-radius: 3px;
  width: 100%;
  margin-top: 14px;
  background: linear-gradient(90deg, #dfe1e5 25%, #e8eaed 50%, #dfe1e5 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skel-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.skel-card {
  background: #eaecef;
  border-radius: 14px;
  overflow: hidden;
}

.skel-card__header {
  height: 42px;
  background: linear-gradient(90deg, #cdd1d6 25%, #d8dce0 50%, #cdd1d6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skel-card__body {
  padding: 20px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (max-width: 599px) {
  .skel-grid {
    grid-template-columns: 1fr;
  }
}

/* ============================================
   ATTACHMENT CONTEXT MENU
   ============================================ */
.atch-menu__card {
  background: #fff;
  border-radius: 12px;
  padding: 6px;
  min-width: 180px;
  font-family: 'Lato', sans-serif;
}

.atch-menu__item {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  color: #334155;
  transition: background-color 0.15s;
}

.atch-menu__item:hover {
  background: #f0f4f8;
}

.atch-menu__item--danger {
  color: #ef4444;
}

.atch-menu__item--danger:hover {
  background: #fef2f2;
}

.atch-menu__divider {
  height: 1px;
  background: #e8eaed;
  margin: 4px 8px;
}

/* ============================================
   FAB & MISC
   ============================================ */
.remarks-fab {
  position: fixed;
  bottom: 28px;
  right: 28px;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: #56bde8;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(86, 189, 232, 0.4);
  transition: all 0.2s ease;
  /* Must clear AppFooter, which is also z-index 5 and comes later in the DOM —
     at a tie the footer wins and swallows the FAB when scrolled to the bottom.
     Still below Vuetify's overlays (200+), so open dialogs cover it. */
  z-index: 6;
}

.remarks-fab:hover {
  background: #3dadd8;
  box-shadow: 0 6px 20px rgba(86, 189, 232, 0.5);
  transform: translateY(-2px);
}

.remarks-fab:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(86, 189, 232, 0.3);
}

/* ============================================
   EMAIL DIALOG
   ============================================ */
.email-modal {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

.email-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 22px 24px 16px;
  border-bottom: 1px solid #f1f5f9;
}

.email-modal__header-left {
  display: flex;
  gap: 14px;
}

.email-modal__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #e0f2fe;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.email-modal__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e293b;
}

.email-modal__subject {
  font-size: 0.82rem;
  color: #64748b;
  margin-top: 2px;
  font-weight: 500;
}

.email-modal__close {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.email-modal__close:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.email-modal__recipients {
  padding: 14px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
}

.email-modal__recipient-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
}

.email-modal__recipient-row:last-child {
  margin-bottom: 0;
}

.email-modal__recipient-label {
  flex: 0 0 24px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  padding-top: 4px;
}

.email-modal__recipient-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.email-modal__chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 6px;
  white-space: nowrap;
}

.email-modal__chip--cc {
  background: #f5f3ff;
  color: #7c3aed;
}

.email-modal__body {
  padding: 20px 24px;
  max-height: 50vh;
  overflow-y: auto;
  font-size: 0.88rem;
  color: #334155;
  line-height: 1.7;
}

.email-modal__body::-webkit-scrollbar {
  width: 5px;
}

.email-modal__body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.email-modal__footer {
  padding: 14px 24px 20px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f1f5f9;
}

.email-modal__btn {
  padding: 10px 24px;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  background: #fff;
  color: #64748b;
  border: 1.5px solid #d1d5db;
  transition: all 0.15s ease;
}

.email-modal__btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.v-btn {
  opacity: 1 !important;
}

.v-btn--active.no-active::before {
  opacity: 0 !important;
}












/* Participant Slider */
.participant-slider {
  margin-top: 4px;
}

.participant-slider__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.participant-slider__badge {
  display: inline-block;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 8px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.participant-slider__nav {
  display: flex;
  gap: 6px;
}

.participant-slider__btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.participant-slider__btn:hover:not(:disabled) {
  background: #f0f9ff;
  border-color: #0ea5e9;
}

.participant-slider__btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.participant-slider__btn .v-icon {
  color: #64748b;
}

.participant-slider__btn:hover:not(:disabled) .v-icon {
  color: #0ea5e9;
}
</style>

<style>
.approve-dialog {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
}

.confirm-dialog {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
}

.remarks-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.flag-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.log-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.email-dialog {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
}

.atch-menu {
  border-radius: 12px !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
  overflow: hidden;
}
</style>
